<template>
    <v-card flat tile>
        <v-row no-gutters class="d-flex">
            <div class="ml-2 grey--text text--darken-1" style="font-size: 12px">
                <template v-if="selectionMode">
                    <span v-if="selected.length"> {{ selected.length }} selected</span>
                </template>
            </div>
            <v-spacer />
            <!-- Toggle Selection Mode -->
            <!-- :disabled="loading" -->
            <v-btn rounded small outlined color="primary" class="mr-2" :disabled="loading || !$store.getters.canWriteAiSuite" @click="toggleSelectionMode">
                <v-icon left small>
                    {{ selectionMode ? "mdi-close" : "mdi-checkbox-multiple-marked-outline" }}
                </v-icon>
                {{ selectionMode ? "End Selection" : "Manage Prompts" }}
            </v-btn>
            <div class="px-2" v-if="selected.length">
                <v-btn small rounded outlined color="primary" class="mr-2" @click="openEditTags"> <v-icon left small>mdi-tag-edit</v-icon> Edit Tags ({{ selected.length }}) </v-btn>
                <!-- bestehender Delete-Button bleibt -->
            </div>
            <!-- Bulk Delete nur im Auswahlmodus -->
            <v-btn rounded v-if="selectionMode" small depressed color="red darken-1" class="mr-2 white--text" :disabled="!selected.length || deleting" :loading="deleting" @click="openBulkDelete">
                <v-icon left small>mdi-delete</v-icon> Delete
            </v-btn>
            <filter-menu
                v-model="filterMenuOpen"
                :activeField="filterMenuField"
                class="mb-1 ml-auto mr-2"
                :fields="filterFields"
                :initialFilters="filters"
                @filtersUpdated="filtersUpdated"
                :dense="true" />
        </v-row>
        <v-data-table
            :headers="headers"
            :items="prompts"
            :loading="loading"
            :server-items-length="total"
            :options.sync="options"
            :items-per-page="itemsPerPage"
            @update:options="onOptionsUpdate"
            :footer-props="footerProps"
            @click:row="onRowInteraction"
            class="elevation-0"
            fixed-header
            height="70vh"
            id="monitoringPromptTable"
            :show-select="selectionMode"
            item-key="_id"
            v-model="selected"
            :disable-sort="false"
            :class="{ 'pt-selection-mode': selectionMode }">
            <!-- Header Filter Icons -->
            <template v-for="(header_entry, index) in headers" v-slot:[`header.${header_entry.value}`]="{ header }">
                <span :key="index">
                    <span :style="{ width: header.width }">
                        <v-btn icon x-small class="mr-1 mb-1" color="primary" style="cursor: pointer" v-if="header.filterable" @click.stop="openFilterMenu(header.value)">
                            <v-icon small>mdi-filter-variant</v-icon>
                        </v-btn>
                        {{ header.value == "sentiment" ? header.value + " " + project.brand.brand_name : header.text }}
                    </span>
                </span>
            </template>

            <!-- Column Slots -->
            <template v-slot:[`item.importDate`]="{ item }">
                {{ formatDate(item.importDate) }}
            </template>

            <template v-slot:[`item.prompt`]="{ item }">
                <span class="prompt-text-cell">{{ item.prompt }}</span>
            </template>
            <template v-slot:[`item.sentiment`]="{ item }">
                <span class="prompt-text-cell">
                    <div class="senitmentchip" :class="getSentimentChipClass(item)">{{ getSentimentForBrand(item) }}</div>
                </span>
            </template>

            <template v-slot:[`item.mergedEntities`]="{ item }">
                <div class="entity-chip-list">
                    <v-tooltip bottom v-for="(entry, idx) in getDisplayEntities(item).slice(0, 4)" :key="entry.key || idx">
                        <template v-slot:activator="{ on, attrs }">
                            <v-chip v-bind="attrs" v-on="on" small outlined class="entity-chip ma-1" :style="entityChipStyle(entry.entity)">
                                <span class="entity-chip-dot" :style="entityDotStyle(entry.entity)"></span>
                                <span class="entity-chip-label">{{ entry.label }}</span>
                            </v-chip>
                        </template>
                        <span>{{ entry.label }}</span>
                    </v-tooltip>

                    <v-tooltip bottom v-if="getDisplayEntities(item).length > 4">
                        <template v-slot:activator="{ on, attrs }">
                            <v-chip v-bind="attrs" v-on="on" small class="entity-chip-more ma-1" outlined color="grey darken-1" text-color="grey darken-1">
                                +{{ getDisplayEntities(item).length - 4 }}
                            </v-chip>
                        </template>
                        <div class="entity-more-tooltip">
                            <div v-for="hidden in getHiddenEntities(item)" :key="hidden.key" class="entity-more-tooltip-row">
                                <span class="entity-more-tooltip-label" :class="{ 'entity-more-tooltip-label--primary': isProjectBrandEntity(hidden) }">
                                    {{ hidden.label }}
                                    <span v-if="isProjectBrandEntity(hidden)" class="entity-more-tooltip-badge">(Primary Brand)</span>
                                </span>
                            </div>
                        </div>
                    </v-tooltip>
                </div>
            </template>

            <template v-slot:[`item.responseText`]="{ item }">
                <span class="prompt-response-text" v-html="renderInlineMarkdown(item.responseText || '')"></span>
            </template>

            <template v-slot:[`item.platform`]="{ item }">
                <v-tooltip bottom v-if="getPlatformInfo(item.platform).logo">
                    <template v-slot:activator="{ on, attrs }">
                        <span class="platform-icon-wrapper" v-bind="attrs" v-on="on" style="display: flex; align-items: center">
                            <span class="platform-icon" v-html="getPlatformInfo(item.platform).logo" style="margin: auto"></span>
                        </span>
                    </template>
                    <span>{{ getPlatformInfo(item.platform).label || item.platform }}</span>
                </v-tooltip>
                <span v-else>{{ item.platform }}</span>
            </template>
            <template v-slot:[`item.sources`]="{ item }">
                <div class="d-flex flex-wrap" style="max-width: 260px; align-items: center">
                    <v-tooltip bottom v-for="(src, i) in (item.sources || []).slice(0, 5)" :key="src.url || src.key || src.domain || i">
                        <template v-slot:activator="{ on, attrs }">
                            <v-chip
                                v-bind="attrs"
                                v-on="on"
                                small
                                outlined
                                class="ma-1"
                                color="grey darken-1"
                                text-color="grey darken-1"
                                @click.stop
                                :href="src.url || null"
                                target="_blank"
                                style="font-size: 11px; height: 20px; line-height: 18px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap"
                                rel="noopener">
                                {{ src.domain || "—" }}
                            </v-chip>
                        </template>
                        <span>{{ src.url || src.key || src.domain }}</span>
                    </v-tooltip>
                    <span v-if="(item.sources || []).length > 5" class="grey--text caption ma-1">+{{ (item.sources || []).length - 5 }}</span>
                </div>
            </template>
            <template v-if="hasTags" v-slot:[`item.tags`]="{ item }">
                <div style="display: flex; flex-wrap: wrap; max-width: 200px; align-items: center">
                    <v-chip
                        v-for="(tag, idx) in item.tags"
                        :key="idx"
                        small
                        outlined
                        class="ma-1"
                        color="primary"
                        text-color="primary"
                        style="font-size: 11px; height: 20px; line-height: 18px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap">
                        {{ tag }}
                    </v-chip>
                </div>
            </template>

            <!-- ACTIONS -->
            <template v-slot:[`item.actions`]="{ item }">
                <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn icon x-small v-bind="attrs" v-on="on" color="red darken-1" :disabled="!$store.getters.canWriteAiSuite" @click.stop="openSingleDelete(item)">
                            <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                    </template>
                    <span>Delete prompt</span>
                </v-tooltip>
            </template>
        </v-data-table>
        <v-progress-linear v-if="loading || !initialFiltersReady" indeterminate height="2"></v-progress-linear>

        <chat-dialog :value="dialog" :item="selectedItem" @input="dialog = $event" :selectedPlatform="selectedPlatform" :available-dates="availableDates" @change-date="onChangeDate" />

        <!-- Delete Confirmation Dialog -->
        <v-dialog v-model="deleteDialog" max-width="480px">
            <v-card>
                <v-card-title class="headline"> Delete {{ deleteMode === "bulk" ? selected.length + " prompts" : "prompt" }}? </v-card-title>
                <v-card-text>
                    <div v-if="deleteMode === 'single'">
                        This action will permanently remove the prompt:
                        <div class="mt-2 pa-2 grey lighten-4 rounded" style="font-size: 13px; line-height: 1.4">
                            {{ deleteTarget && deleteTarget.prompt }}
                        </div>
                    </div>
                    <div v-else>
                        This action will permanently remove
                        <strong>{{ selected.length }}</strong> prompts.
                    </div>
                    <div v-if="deleteReleaseHint" class="mt-3" style="font-size: 13px">
                        <v-icon x-small class="mr-1">mdi-database-outline</v-icon>
                        {{ deleteReleaseHint }}
                    </div>
                    <!-- Historie: Standard ist behalten. Loeschen baut die betroffenen Tage im Backend neu auf
                         (brand_visibility, Sources, Shopping), sonst laeuft die Sichtbarkeit alter Tage ueber 100 %. -->
                    <v-radio-group v-model="deleteHistory" class="mt-4 mb-0" hide-details dense :disabled="deleting">
                        <v-radio :value="false">
                            <template v-slot:label>
                                <div style="font-size: 13px; line-height: 1.4">
                                    <strong>Keep history</strong>
                                    <div class="grey--text">Stop tracking only. Past answers and visibility stay as they were crawled.</div>
                                </div>
                            </template>
                        </v-radio>
                        <v-radio :value="true">
                            <template v-slot:label>
                                <div style="font-size: 13px; line-height: 1.4">
                                    <strong>Delete history too</strong>
                                    <div class="grey--text">Remove past answers of these prompts and recalculate the affected days.</div>
                                </div>
                            </template>
                        </v-radio>
                    </v-radio-group>
                    <v-alert dense outlined type="warning" class="mt-4 mb-0"> This cannot be undone. </v-alert>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="deleteDialog = false" :disabled="deleting">Cancel</v-btn>
                    <v-btn color="red darken-1" dark :loading="deleting" :disabled="deleting" @click="performDelete">
                        <v-icon left small>mdi-delete</v-icon>
                        Delete
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Dialog: Tags bearbeiten -->
        <v-dialog v-model="editTagsDialog" max-width="720px">
            <v-card>
                <v-card-title class="py-3 white--text primarycolor">
                    <v-icon color="white" left small class="mr-2">mdi-tag-edit</v-icon>
                    Edit tags
                    <v-spacer />
                    <div class="caption white--text">
                        <v-chip color="white" small outlined>{{ selected.length }} selected</v-chip>
                    </div>
                </v-card-title>

                <v-card-subtitle class="pt-0 pb-2 white--text primarycolor"> Assign, remove, or replace tags. You can add new tags via free text. </v-card-subtitle>

                <v-divider></v-divider>

                <v-card-text>
                    <!-- Mode switch -->
                    <div class="mb-3 mt-4 d-flex align-center">
                        <div class="overline mr-3">Mode</div>
                        <v-btn-toggle dense v-model="tagSetExact" mandatory class="tag-op-toggle">
                            <v-btn small rounded :value="false" :class="{ 'is-active': !tagSetExact }" text> <v-icon left small>mdi-plus-circle-outline</v-icon> Add/Remove </v-btn>
                            <v-btn small rounded :value="true" :class="{ 'is-active': tagSetExact }" class="ml-2" text> <v-icon left small>mdi-sync</v-icon> Exact replace </v-btn>
                        </v-btn-toggle>
                        <v-spacer />
                        <!-- <v-chip small outlined class="ml-2" v-if="tagsFromSelectionList.length"> {{ tagsFromSelectionList.length }} tag(s) in selection </v-chip> -->
                    </div>

                    <!-- REPLACE: exact set -->
                    <div v-if="tagSetExact">
                        <v-combobox
                            v-model="tagSet"
                            :items="availableTags"
                            item-text="label"
                            item-value="label"
                            return-object
                            label="Tags (exact set)"
                            multiple
                            chips
                            deletable-chips
                            clearable
                            rounded
                            filled
                            hide-details
                            dense>
                            <!-- Tag dots in selection -->
                            <template v-slot:selection="{ item, selected, attrs }">
                                <v-chip v-bind="attrs" :input-value="selected" small class="ma-1" outlined>
                                    <span class="tag-color-dot" :style="tagDotStyle(item)"></span>
                                    {{ item.label || item }}
                                </v-chip>
                            </template>
                            <!-- Tag dots in dropdown -->
                            <template v-slot:item="{ item, on, attrs }">
                                <v-list-item v-on="on" v-bind="attrs">
                                    <v-list-item-content>
                                        <v-list-item-title>
                                            <span class="tag-color-dot" :style="tagDotStyle(item)"></span>
                                            {{ item.label || item }}
                                        </v-list-item-title>
                                    </v-list-item-content>
                                </v-list-item>
                            </template>
                        </v-combobox>
                        <div class="caption mt-2 grey--text">Free text allowed: type and press Enter.</div>
                    </div>

                    <!-- ADD/REMOVE -->
                    <div v-else class="d-flex flex-wrap" style="gap: 16px">
                        <!-- Add -->
                        <div style="flex: 1 1 320px; min-width: 280px">
                            <div class="overline mb-1">Add</div>
                            <v-combobox
                                v-model="tagAdd"
                                :items="availableTags"
                                item-text="label"
                                item-value="label"
                                return-object
                                label="Add tags"
                                multiple
                                deletable-chips
                                rounded
                                filled
                                chips
                                clearable
                                hide-details
                                dense>
                                <template v-slot:selection="{ item, selected, attrs }">
                                    <v-chip v-bind="attrs" :input-value="selected" small class="ma-1" outlined>
                                        <span class="tag-color-dot" :style="tagDotStyle(item)"></span>
                                        {{ item.label || item }}
                                    </v-chip>
                                </template>
                                <template v-slot:item="{ item, on, attrs }">
                                    <v-list-item v-on="on" v-bind="attrs">
                                        <v-list-item-content>
                                            <v-list-item-title>
                                                <span class="tag-color-dot" :style="tagDotStyle(item)"></span>
                                                {{ item.label || item }}
                                            </v-list-item-title>
                                        </v-list-item-content>
                                    </v-list-item>
                                </template>
                            </v-combobox>
                            <div class="caption mt-2 grey--text">Free text allowed: type and press Enter.</div>
                        </div>

                        <!-- Remove -->
                        <div style="flex: 1 1 320px; min-width: 280px">
                            <div class="overline mb-1">Remove</div>
                            <v-combobox
                                v-model="tagRemove"
                                :items="tagsFromSelectionList"
                                :disabled="!selected.length || tagsFromSelectionList.length === 0"
                                label="Remove tags from selection"
                                multiple
                                deletable-chips
                                rounded
                                filled
                                chips
                                clearable
                                hide-details
                                dense>
                                <!-- Dot chips in selection -->
                                <template v-slot:selection="{ item, selected, attrs }">
                                    <v-chip v-bind="attrs" :input-value="selected" small class="ma-1" outlined>
                                        <span class="tag-color-dot" :style="tagDotStyle(item)"></span>
                                        {{ item }}
                                    </v-chip>
                                </template>
                                <!-- Dot items in dropdown -->
                                <template v-slot:item="{ item, on, attrs }">
                                    <v-list-item v-on="on" v-bind="attrs">
                                        <v-list-item-content>
                                            <v-list-item-title>
                                                <span class="tag-color-dot" :style="tagDotStyle(item)"></span>
                                                {{ item }}
                                            </v-list-item-title>
                                        </v-list-item-content>
                                    </v-list-item>
                                </template>
                            </v-combobox>
                            <div class="caption mt-2 grey--text" v-if="!selected.length">Select prompts first.</div>
                            <div class="caption mt-2 grey--text" v-else-if="selected.length && !tagsFromSelectionList.length">Selected prompts have no tags.</div>
                        </div>
                    </div>
                </v-card-text>

                <v-divider></v-divider>

                <v-card-actions>
                    <v-spacer />
                    <v-btn rounded text @click="editTagsDialog = false">Cancel</v-btn>
                    <v-btn small text class="mr-2" @click="resetTagEdit" v-if="tagAdd.length || tagRemove.length || tagSet.length"> <v-icon left small>mdi-backspace-outline</v-icon> Reset </v-btn>
                    <v-btn color="primary" rounded @click="applyTagChanges" :disabled="applyDisabled">
                        <v-icon left small>mdi-content-save</v-icon>
                        Apply
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Download Dialog -->
        <v-dialog v-model="downloadDialog" max-width="460px">
            <v-card>
                <v-card-title class="headline">Download Prompts</v-card-title>
                <v-card-text>
                    <div class="mb-3">Choose what should be exported:</div>
                    <v-radio-group v-model="downloadScope" hide-details>
                        <v-radio label="Current page" value="current" />
                        <v-radio :label="`All pages (${total || 0} rows)`" value="all" />
                    </v-radio-group>

                    <div v-if="downloadScope === 'all'" class="mt-4">
                        <div class="mb-2">Date range (based on available dates):</div>
                        <v-row dense>
                            <v-col cols="12" sm="6">
                                <v-select v-model="downloadFromDate" :items="downloadDateOptions" label="From" dense outlined hide-details :disabled="downloading || !downloadDateOptions.length" />
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-select v-model="downloadToDate" :items="downloadDateOptions" label="To" dense outlined hide-details :disabled="downloading || !downloadDateOptions.length" />
                            </v-col>
                        </v-row>
                        <v-alert v-if="isDownloadDateRangeInvalid" dense outlined type="warning" class="mt-3 mb-0">Please select a valid date range.</v-alert>
                    </div>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text :disabled="downloading" @click="cancelDownloadDialog">Cancel</v-btn>
                    <v-btn color="primary" :loading="downloading" :disabled="downloading || (downloadScope === 'all' && isDownloadDateRangeInvalid)" @click="confirmDownloadDialog">Download</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-card>
</template>

<script>
    import OpenAISVG from "@/assets/openai-svgrepo-com.svg"
    import { AMAZON_RUFUS_LOGO } from "../../utils/platformMeta"
    import chatDialog from "./chatDialog.vue"
    import MarkdownIt from "markdown-it"
    import FilterMenu from "../monitoring/FilterMenu.vue"
    import { getBrandColorFromName } from "../../utils/brandColor"
    import { releaseHint, entriesCreditUsage } from "../helper/projectStatus"
    export default {
        inject: {
            // Von AIProject bereitgestellt: öffnet das Prompt-Detail als Overlay statt per Route
            aiSuiteOpenPromptDetail: { default: null }
        },
        props: {
            brand: { type: Object, required: true }, // Die aktuell ausgewählte Brand
            isActive: { type: Boolean, default: false },
            search: String,
            selectedDates: Array,
            project_id: String,
            exportData: Number,
            platforms: Array,
            sentimentAvailable: { type: Boolean, default: false },
            brands: Array,
            availableDates: Array,
            selectedPlatform: Object,
            project: Object,
            hasTags: { type: Boolean, default: false },
            tags: Array,
            availableTags: { type: Array, default: () => [] },
            tagsOperator: { type: String, default: "or" }, // 'or' | 'and'
            initialFilters: {
                type: Array,
                default: null
            },
            initialFiltersReady: {
                type: Boolean,
                default: true
            }
        },
        components: {
            OpenAISVG,
            chatDialog,
            FilterMenu
        },
        data() {
            return {
                selectionMode: false,
                selectedBrand: this.brand,
                prompts: [],
                options: {
                    page: 1,
                    itemsPerPage: 25,
                    sortBy: [],
                    sortDesc: []
                },
                // footerProps: {
                //     itemsPerPageOptions: [10, 25, 50, 100],
                //     showFirstLastPage: true
                // },
                itemsPerPage: 25,
                filterMenuOpen: false,
                filterMenuField: null,
                total: 0,
                loading: false,
                dialog: false,
                selectedItem: {},
                sortDesc: true,
                sortBy: "importDate",
                page: 1,
                md: new MarkdownIt({ breaks: false }),
                filters: [],
                selected: [], // NEU: ausgewählte Zeilen (IDs)
                deleteDialog: false,
                deleteTarget: null,
                deleteMode: "single", // 'single' | 'bulk'
                deleteHistory: false, // false: Prompt nur aus dem Tracking nehmen, true: auch Antworten/Auswertungen alter Tage loeschen
                deleting: false,
                editTagsDialog: false, // Dialog sichtbar
                tagAdd: [], // Tags hinzufügen
                tagRemove: [], // Tags entfernen
                tagSetExact: false, // "exakt setzen"-Modus
                tagSet: [], // Tags für Set-Modus
                downloadDialog: false,
                downloadScope: "current",
                downloading: false,
                _downloadResolver: null,
                downloadFromDate: null,
                downloadToDate: null
            }
        },
        computed: {
            // welche Credits die Löschung freigibt (Prompts x Intervallfaktor x Plattformen); leer bei Pause/Demo
            deleteReleaseHint() {
                const count = this.deleteMode === "single" ? 1 : (this.selected || []).length
                return this.project ? releaseHint(this.project, entriesCreditUsage(this.project, count)) : ""
            },
            applyDisabled() {
                if (this.tagSetExact) return this.tagSet.length === 0
                const hasAdd = this.tagAdd.length > 0
                const hasRem = this.tagRemove.length > 0
                return !(hasAdd || hasRem)
            },
            tagsFromSelectionList() {
                const set = new Set()
                ;(this.selected || []).forEach((row) => {
                    if (Array.isArray(row.tags)) {
                        row.tags.forEach((tag) => {
                            const s = (tag || "").toString().trim()
                            if (s) set.add(s)
                        })
                    }
                })
                return Array.from(set).sort((a, b) => a.localeCompare(b))
            },
            isAllPlatforms() {
                return this.selectedPlatform && this.selectedPlatform.key === "all"
            },
            filterFields() {
                let fields = [
                    { name: "Prompt", key: "prompt", type: "text" },
                    { name: "Response", key: "responseText", type: "text" },
                    {
                        name: "Brand Mention",
                        key: "mergedEntities",
                        type: "array",
                        options: {
                            values: this.brands ? this.brands.map((b) => b.canonicalName) : []
                        },
                        multiple: true
                    },
                    { name: "Sources", key: "sources", type: "text" },
                    // { name: "Sentiment (%)", key: "sentiment", type: "number" },
                    // { name: "Platform", key: "platform", type: "select", options: { values: this.platforms ? this.platforms : [] }, multiple: true },
                    { name: "Tags", key: "tags", type: "select", options: { values: this.hasTags ? this.$parent.availableTags : [] }, multiple: true }
                ]
                if (this.sentimentAvailable) {
                    fields.splice(4, 0, { name: "Sentiment (%)", key: "sentiment", type: "number" })
                }
                return fields
            },
            footerProps() {
                return {
                    itemsPerPageOptions: [10, 25, 50, 100],
                    showFirstLastPage: true
                }
            },
            headers() {
                let h = [
                    { text: "Platform", value: "platform", align: "start", filterable: false },
                    { text: "Prompt", value: "prompt", align: "start", filterable: true },
                    { text: "Response", value: "responseText", class: "col-response", cellClass: "col-response", align: "start", filterable: true },
                    { text: "Entities", value: "mergedEntities", width: "300px", minWidth: "320px", align: "start", filterable: true },
                    // { text: "Sentiment", value: "sentiment", align: "start", filterable: true },
                    { text: "Sources", value: "sources", align: "start", filterable: true }, // <--- Korrigiert
                    { text: "Updated", value: "importDate", align: "start", filterable: false }
                ]
                if (this.hasTags) {
                    h.splice(4, 0, { text: "Tags", value: "tags", align: "start", filterable: true })
                }
                if (this.sentimentAvailable) {
                    h.splice(4, 0, { text: `Sentiment ${this.project?.brand?.brand_name || ""}`, value: "sentiment", align: "start", filterable: true })
                }
                // h.push({ text: "Actions", value: "actions", align: "center", sortable: false, filterable: false, width: "80px" })
                return h
            },
            downloadDateOptions() {
                const arr = Array.isArray(this.availableDates) ? this.availableDates : []
                const norm = arr
                    .map((d) => {
                        const dt = new Date(d)
                        return isNaN(dt) ? null : dt.toISOString().slice(0, 10)
                    })
                    .filter(Boolean)
                return Array.from(new Set(norm)).sort()
            },
            isDownloadDateRangeInvalid() {
                if (!this.downloadDateOptions.length) return false
                if (!this.downloadFromDate || !this.downloadToDate) return true
                return this.downloadFromDate > this.downloadToDate
            }
        },
        methods: {
            initDownloadDateRange() {
                const options = this.downloadDateOptions
                if (!options.length) {
                    this.downloadFromDate = null
                    this.downloadToDate = null
                    return
                }

                const selected = Array.isArray(this.selectedDates)
                    ? this.selectedDates
                          .map((d) => {
                              const dt = new Date(d)
                              return isNaN(dt) ? null : dt.toISOString().slice(0, 10)
                          })
                          .filter(Boolean)
                          .sort()
                    : []

                const selectedFrom = selected[0]
                const selectedTo = selected[selected.length - 1] || selectedFrom

                this.downloadFromDate = selectedFrom && options.includes(selectedFrom) ? selectedFrom : options[0]
                this.downloadToDate = selectedTo && options.includes(selectedTo) ? selectedTo : options[options.length - 1]

                if (this.downloadFromDate > this.downloadToDate) {
                    this.downloadToDate = this.downloadFromDate
                }
            },
            buildSinglePlatformDateRange(selectedDays = null) {
                if (!this.project || !this.selectedPlatform?.key || this.selectedPlatform.key === "all") return null
                if (!Array.isArray(selectedDays) || !selectedDays.length) return null

                const map = {
                    openai: "processed_dates_ai_tracking_openai",
                    chatgpt: "processed_dates_ai_tracking_chatgpt",
                    google: "processed_dates",
                    google_ai_mode: "processed_dates_ai_tracking_google_ai_mode",
                    gemini: "processed_dates_ai_tracking_gemini",
                    gemini_api: "processed_dates_ai_tracking_gemini_api",
                    claude: "processed_dates_ai_tracking_claude",
                    perplexity: "processed_dates_ai_tracking_perplexity",
                    grok: "processed_dates_ai_tracking_grok",
                    microsoft_copilot: "processed_dates_ai_tracking_microsoft_copilot",
                    amazon_rufus: "processed_dates_ai_tracking_amazon_rufus"
                }

                const field = map[this.selectedPlatform.key]
                const arr = field && Array.isArray(this.project[field]) ? this.project[field] : []
                if (!arr.length) return null

                const toUtcDay = (d) => {
                    const dt = new Date(d)
                    return isNaN(dt) ? null : dt.toISOString().slice(0, 10)
                }

                const days = selectedDays.map(toUtcDay).filter(Boolean).sort()
                if (!days.length) return null
                const firstDay = days[0]
                const lastDay = days[days.length - 1]

                let earliest = null
                let latest = null
                arr.forEach((d) => {
                    const dt = new Date(d)
                    if (isNaN(dt)) return
                    const day = dt.toISOString().slice(0, 10)
                    if (day === firstDay && (!earliest || dt < earliest)) earliest = dt
                    if (day === lastDay && (!latest || dt > latest)) latest = dt
                })

                const from = earliest ? earliest.toISOString() : `${firstDay}T00:00:00.000Z`
                const to = latest ? latest.toISOString() : `${lastDay}T23:59:59.999Z`
                if (new Date(from) > new Date(to)) return null
                return { from, to }
            },
            getSentimentScore(item) {
                if (!this.project || !item || !Array.isArray(item.mergedEntities)) return null

                const normalizeBrandToken = (value) => {
                    return (value || "")
                        .toString()
                        .normalize("NFKD")
                        .replace(/[\u2018\u2019\u0060\u00B4]/g, "'")
                        .replace(/['’`´]/g, "")
                        .replace(/\s+/g, " ")
                        .trim()
                        .toLowerCase()
                }

                const brandTokens = new Set()
                const addBrandToken = (v) => {
                    const n = normalizeBrandToken(v)
                    if (n) brandTokens.add(n)
                }

                addBrandToken(this.project?.brand?.brand_name)
                addBrandToken(this.brand?.brand_name)
                ;(Array.isArray(this.project?.brand?.mentions) ? this.project.brand.mentions : []).forEach(addBrandToken)
                ;(Array.isArray(this.brand?.mentions) ? this.brand.mentions : []).forEach(addBrandToken)

                if (!brandTokens.size) return null

                const entity = item.mergedEntities.find((e) => {
                    const candidates = [e?.canonicalName, e?.name, e?.brand_name]
                    if (Array.isArray(e?.mentions)) {
                        e.mentions.forEach((m) => candidates.push(typeof m === "string" ? m : m?.text?.content || ""))
                    }

                    return candidates.some((candidate) => {
                        const normalized = normalizeBrandToken(candidate)
                        return normalized && brandTokens.has(normalized)
                    })
                })
                if (entity && entity.sentiment_score != null) {
                    const score = Number(entity.sentiment_score)
                    return Number.isFinite(score) ? score : null
                }
                return null
            },
            getSentimentForBrand(item) {
                const score = this.getSentimentScore(item)
                if (score === null) return "-"
                return (score * 100).toFixed(0)
            },
            getSentimentChipClass(item) {
                const score = this.getSentimentScore(item)
                if (score === null) return "senitmentchip--neutral"
                if (score > 0) return "senitmentchip--positive"
                if (score < 0) return "senitmentchip--negative"
                return "senitmentchip--neutral"
            },
            async onChangeDate(dayOrIso) {
                if (!this.selectedItem) return
                const projectId = this.project_id || this.$route?.params?.id
                if (!projectId) return

                const companyId = localStorage.company_id
                const base =
                    JSON.parse(process.env.VUE_APP_MONITORING_PROXY_COMPANY_IDS).indexOf(this.CryptoJS.AES.decrypt(companyId, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)) ===
                    -1
                        ? process.env.VUE_APP_MONITORING_API_URL
                        : process.env.VUE_APP_APIURL + "/monitoring-api"
                const url = `${base}/ai-suite/promptresponses/get`

                // auf Tages-String normalisieren (YYYY-MM-DD, UTC)
                const toDay = (d) => {
                    const dt = new Date(d)
                    if (isNaN(dt)) return null
                    const y = dt.getUTCFullYear()
                    const m = String(dt.getUTCMonth() + 1).padStart(2, "0")
                    const da = String(dt.getUTCDate()).padStart(2, "0")
                    return `${y}-${m}-${da}`
                }
                const day = typeof dayOrIso === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dayOrIso) ? dayOrIso : toDay(dayOrIso)
                if (!day) return

                const post_data = {
                    company_id: companyId,
                    project_id: projectId,
                    platform: this.selectedItem.platform,
                    sort: { importDate: -1 },
                    skip: 0,
                    limit: 10,
                    search: this.selectedItem.prompt,
                    importDateRange: {
                        from: `${day}T00:00:00.000Z`,
                        to: `${day}T23:59:59.999Z`
                    }
                }
                const detailBrandName = this.project?.brand?.brand_name || this.brand?.brand_name || null
                const detailBrandMentions = Array.isArray(this.project?.brand?.mentions) ? this.project.brand.mentions : Array.isArray(this.brand?.mentions) ? this.brand.mentions : []
                if (detailBrandName) post_data.brandName = detailBrandName
                if (detailBrandMentions.length) post_data.brandMentions = detailBrandMentions
                if (this.tags?.length) {
                    post_data.tags = this.tags
                    post_data.tagsOperator = this.tagsOperator
                }

                try {
                    const res = await this.$helpers.axiosPost(url, post_data)
                    const items = Array.isArray(res?.data?.responses) ? res.data.responses : []
                    const next = items.find((it) => (it?.prompt || "") === (this.selectedItem?.prompt || "")) || items[0]
                    if (next) this.selectedItem = { ...next }
                    else this.$emit("createNotification", "No response for selected date", "mdi-alert-circle-outline", "warning")
                } catch (e) {
                    console.error("onChangeDate failed:", e)
                    this.$emit("createNotification", "Loading response failed", "mdi-alert-circle-outline", "error")
                }
            },

            async applyTagChanges() {
                if (!this.$store.getters.canWriteAiSuite) return
                if (!this.selected.length) {
                    this.editTagsDialog = false
                    return
                }

                const promptSet = new Set((this.selected || []).map((r) => (r?.prompt || "").toString().trim()).filter(Boolean))
                const prompts = Array.from(promptSet)
                if (!prompts.length) {
                    this.editTagsDialog = false
                    return
                }

                // Nur Objekt->String abbilden, Normalisierung/Dedupe übernimmt Backend
                const toLabel = (t) => {
                    if (typeof t === "string") return t.trim()
                    if (t && typeof t === "object") return (t.label ?? t.name ?? t.id ?? "").toString().trim()
                    return ""
                }
                const mapTags = (arr) => (arr || []).map(toLabel).filter(Boolean)

                const payload = {
                    company_id: localStorage.company_id,
                    project_id: this.project_id || this.$route.params.id,
                    prompts,
                    platform: this.selectedPlatform && this.selectedPlatform.key !== "all" ? this.selectedPlatform.key : null,
                    ...(this.tagSetExact ? { setTags: mapTags(this.tagSet) } : { addTags: mapTags(this.tagAdd), removeTags: mapTags(this.tagRemove) })
                }

                const hasSet = Array.isArray(payload.setTags)
                const hasAdd = Array.isArray(payload.addTags) && payload.addTags.length
                const hasRem = Array.isArray(payload.removeTags) && payload.removeTags.length
                if (!hasSet && !hasAdd && !hasRem) {
                    this.editTagsDialog = false
                    return
                }

                const url =
                    (JSON.parse(process.env.VUE_APP_MONITORING_PROXY_COMPANY_IDS).indexOf(
                        this.CryptoJS.AES.decrypt(localStorage.company_id, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                    ) == -1
                        ? process.env.VUE_APP_MONITORING_API_URL
                        : process.env.VUE_APP_APIURL + "/monitoring-api") + "/ai-suite/modify/tags"
                await this.$helpers.axiosPost(url, payload)

                // Optimistic UI (lokal weiterhin Duplikate vermeiden)
                const pSet = new Set(prompts)
                const platformKey = payload.platform || null
                const add = hasSet ? [] : payload.addTags || []
                const rem = hasSet ? [] : payload.removeTags || []
                const set = hasSet ? payload.setTags || [] : null
                const remSet = new Set(rem)

                this.prompts = (this.prompts || []).map((row) => {
                    const samePrompt = pSet.has((row?.prompt || "").toString().trim())
                    const samePlatform = !platformKey || row?.platform === platformKey
                    if (!samePrompt || !samePlatform) return row

                    let tags = Array.isArray(row.tags) ? [...row.tags] : []
                    if (set) {
                        tags = [...new Set(set)]
                    } else {
                        add.forEach((t) => {
                            if (!tags.includes(t)) tags.push(t)
                        })
                        if (remSet.size) tags = tags.filter((t) => !remSet.has(t))
                    }
                    return { ...row, tags }
                })

                this.$emit && this.$emit("createNotification", "Tags updated", "mdi-tag-edit", "primary")
                this.editTagsDialog = false
                this.selected = []
                this.$emit("tagsUpdated")
                this.resetTagEdit()
            },
            resetTagEdit() {
                this.tagAdd = []
                this.tagRemove = []
                this.tagSet = []
            },
            // Farbdot für Tags (unterstützt Objekt {label,color} oder String)
            tagDotStyle(item) {
                const label = typeof item === "string" ? item : (item?.label ?? item?.name ?? item?.id ?? "").toString()
                const color = item && item.color ? item.color : this.colorForString(label)
                return {
                    backgroundColor: color,
                    borderColor: this.shadeColor(color, -15)
                }
            },
            colorForString(str) {
                let hash = 0
                for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash)
                const hue = Math.abs(hash) % 360
                return `hsl(${hue}, 70%, 55%)`
            },
            shadeColor(hsl, percent) {
                // hsl(h, s%, l%) -> l justieren
                const m = /hsl\(\s*([\d.]+),\s*([\d.]+)%,\s*([\d.]+)%\s*\)/i.exec(hsl)
                if (!m) return hsl
                const h = +m[1],
                    s = +m[2],
                    l = Math.max(0, Math.min(100, +m[3] + percent))
                return `hsl(${h}, ${s}%, ${l}%)`
            },
            openEditTags() {
                if (!this.$store.getters.canWriteAiSuite) return
                this.tagAdd = []
                this.tagRemove = []
                this.tagSet = []
                this.tagSetExact = false
                this.editTagsDialog = true
            },
            normalizeEntityToken(value) {
                return (value || "")
                    .toString()
                    .normalize("NFKD")
                    .replace(/[\u2018\u2019\u0060\u00B4]/g, "'")
                    .replace(/['’`´]/g, "")
                    .replace(/\s+/g, " ")
                    .trim()
                    .toLowerCase()
            },
            toggleSelectionMode() {
                // AI-Suite-Rolle "nur lesen": Auswahlmodus (Tags bearbeiten, Löschen) ist gesperrt
                if (!this.$store.getters.canWriteAiSuite) return
                if (this.selectionMode) {
                    // beenden
                    this.selectionMode = false
                    this.selected = []
                } else {
                    this.selectionMode = true
                }
            },
            onRowInteraction(item) {
                // Im Auswahlmodus keine Detail-Ansicht öffnen
                if (this.selectionMode) return

                const projectId = this.project?._id || this.project_id || this.$route?.params?.id
                if (!projectId) return

                const dateRange = Array.isArray(this.selectedDates) ? this.selectedDates.filter(Boolean) : []
                const routeBrand = this.getRouteBrandPayload()
                const query = {
                    task_id: item.task_id, // falls vorhanden
                    prompt: item.prompt, // Fallback
                    platform: item.platform || (this.selectedPlatform && this.selectedPlatform.key !== "all" ? this.selectedPlatform.key : undefined),
                    dates: dateRange.length ? JSON.stringify(dateRange) : undefined,
                    brand: routeBrand || undefined
                }

                // Bevorzugt als Overlay im AIProject öffnen (kein Routenwechsel, View bleibt erhalten)
                if (this.aiSuiteOpenPromptDetail) {
                    this.aiSuiteOpenPromptDetail({ ...query, project_id: projectId })
                    return
                }

                // Merken, dass beim Zurück-Navigieren aus dem Prompt-Detail wieder die
                // Prompts-Tab angezeigt werden soll (wird in AIProject beim Mount/Activate ausgewertet).
                try {
                    sessionStorage.setItem(`ai_suite_project_return_section_${projectId}`, "prompts")
                } catch (e) {
                    // ignore storage errors
                }

                this.$router.push({
                    name: "PromptDetail",
                    params: { id: projectId },
                    query
                })
            },
            getRouteBrandPayload() {
                const matched = this.findMatchedBrandByProjectName()
                if (!matched) return null

                try {
                    return JSON.stringify({
                        canonicalName: matched.canonicalName || matched.brand_name || matched.brand || matched.name || null,
                        brand_name: matched.brand_name || matched.brand || matched.canonicalName || matched.name || null,
                        brand: matched.brand || matched.brand_name || matched.canonicalName || matched.name || null,
                        mentions: Array.isArray(matched.mentions) ? matched.mentions : []
                    })
                } catch (e) {
                    return null
                }
            },
            findMatchedBrandByProjectName() {
                const projectBrandName = this.project?.brand?.brand_name || this.brand?.brand_name || this.brand?.name || ""
                const projectToken = this.normalizeEntityToken(projectBrandName)
                if (!projectToken || !Array.isArray(this.brands) || !this.brands.length) return null

                const getMentionValue = (mention) => {
                    if (mention == null) return ""
                    if (typeof mention === "string" || typeof mention === "number") return String(mention)
                    return mention?.text?.content || mention?.content || mention?.canonicalName || mention?.name || mention?.brand || mention?.entity || ""
                }

                return (
                    this.brands.find((entry) => {
                        const mentions = Array.isArray(entry?.mentions) ? entry.mentions : []
                        return mentions.some((mention) => this.normalizeEntityToken(getMentionValue(mention)) === projectToken)
                    }) || null
                )
            },
            // onRowInteraction(item) {
            //     // Im Auswahlmodus keine Detail-Ansicht öffnen
            //     console.log(item.mergedEntities.map((i) => i.name))
            //     if (this.selectionMode) return
            //     this.openDialog(item)
            // },
            openSingleDelete(item) {
                if (!this.$store.getters.canWriteAiSuite) return
                this.deleteMode = "single"
                this.deleteTarget = item
                this.deleteHistory = false
                this.deleteDialog = true
            },
            openBulkDelete() {
                if (!this.$store.getters.canWriteAiSuite || !this.selected.length) return
                this.deleteMode = "bulk"
                this.deleteTarget = null
                this.deleteHistory = false
                this.deleteDialog = true
            },
            async performDelete() {
                if (this.deleting || !this.$store.getters.canWriteAiSuite) return
                console.log(this.selected)
                const ids = this.deleteMode === "single" ? [this.deleteTarget?._id] : this.selected.map((p) => p._id)
                const prompts = this.deleteMode === "single" ? [this.deleteTarget?.prompt] : this.selected.map((p) => p.prompt)
                const validIds = ids.filter(Boolean)
                const validPrompts = prompts.filter(Boolean)
                if (!validIds.length || !validPrompts.length) {
                    this.deleteDialog = false
                    return
                }
                this.deleting = true
                try {
                    // Beispiel-Endpoint (ggf. anpassen falls anders benannt)
                    const url =
                        (JSON.parse(process.env.VUE_APP_MONITORING_PROXY_COMPANY_IDS).indexOf(
                            this.CryptoJS.AES.decrypt(localStorage.company_id, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                        ) == -1
                            ? process.env.VUE_APP_MONITORING_API_URL
                            : process.env.VUE_APP_APIURL + "/monitoring-api") + "/ai-suite/prompts/delete"
                    const post_data = {
                        company_id: localStorage.company_id,
                        project_id: this.project_id || this.$route.params.id,
                        prompt_ids: validIds,
                        prompts: validPrompts,
                        user_id: localStorage.id,
                        delete_history: this.deleteHistory === true
                    }
                    await this.$helpers.axiosPost(url, post_data)

                    // Optimistisches Entfernen
                    this.prompts = this.prompts.filter((p) => !validPrompts.includes(p.prompt))
                    this.total = Math.max(this.total - validPrompts.length, 0)
                    if (this.deleteMode === "bulk") {
                        this.selected = []
                    }
                    this.$emit(
                        "createNotification",
                        validPrompts.length + " prompt(s) deleted" + (this.deleteHistory ? ", history is being recalculated in the background" : ""),
                        "mdi-delete",
                        "primary"
                    )
                } catch (e) {
                    console.error(e)
                    this.$emit("createNotification", "Delete failed", "mdi-alert-circle-outline", "error")
                } finally {
                    this.deleting = false
                    this.deleteDialog = false
                }
            },
            csvCell(value) {
                if (value === undefined || value === null) return ""
                return `"${String(value).replace(/"/g, '""')}"`
            },
            getMergedEntitiesForExport(item) {
                const direct = Array.isArray(item?.mergedEntities) ? item.mergedEntities : []
                if (direct.length) return direct

                const responses = Array.isArray(item?.responses) ? item.responses : []
                if (Array.isArray(responses[1]?.mergedEntities) && responses[1].mergedEntities.length) {
                    return responses[1].mergedEntities
                }
                const withEntities = responses.find((r) => Array.isArray(r?.mergedEntities) && r.mergedEntities.length)
                return Array.isArray(withEntities?.mergedEntities) ? withEntities.mergedEntities : []
            },
            getEntitySentimentValue(entity) {
                const raw = entity?.sentiment_score ?? entity?.sentimentScore ?? entity?.sentiment ?? null
                const num = Number(raw)
                return Number.isFinite(num) ? num : ""
            },
            sentimentLabelFromValue(value) {
                const num = Number(value)
                if (!Number.isFinite(num)) return ""
                if (num > 0) return "positive"
                if (num < 0) return "negative"
                return "neutral"
            },
            mentionTextValue(mention) {
                if (typeof mention === "string") return mention
                if (!mention || typeof mention !== "object") return ""
                return mention?.text?.snippet || mention?.text?.content || mention?.snippet || mention?.content || mention?.name || mention?.entity || mention?.brand || ""
            },
            getEntityIdentityTokens(entity) {
                if (!entity) return []
                const candidates = [entity?.canonicalName, entity?.name, entity?.brand_name]
                if (Array.isArray(entity?.mentions)) {
                    entity.mentions.forEach((m) => {
                        if (typeof m === "string") {
                            candidates.push(m)
                        } else {
                            candidates.push(m?.name, m?.entity, m?.brand, m?.canonicalName)
                        }
                    })
                }
                return Array.from(new Set(candidates.map((v) => this.normalizeEntityToken(v)).filter(Boolean)))
            },
            findEntityByIdentity(targetEntity, entityList = []) {
                const list = Array.isArray(entityList) ? entityList : []
                if (!targetEntity || !list.length) return null

                const targetTokens = new Set(this.getEntityIdentityTokens(targetEntity))
                if (!targetTokens.size) return null

                return (
                    list.find((candidate) => {
                        const candidateTokens = this.getEntityIdentityTokens(candidate)
                        return candidateTokens.some((t) => targetTokens.has(t))
                    }) || null
                )
            },
            getEntityMentionSnippet(entity) {
                const mentions = Array.isArray(entity?.mentions) ? entity.mentions : []
                if (!mentions.length) return ""

                const firstMentionWithText = mentions.find((mention) => {
                    const txt = this.mentionTextValue(mention)
                    return Boolean((txt || "").toString().trim())
                })

                if (!firstMentionWithText) return ""
                return this.mentionTextValue(firstMentionWithText)
            },
            getEntityExportTriples(item) {
                const primaryEntities = this.getMergedEntitiesForExport(item)
                const responseEntities = Array.isArray(item?.responses?.[1]?.mergedEntities) ? item.responses[1].mergedEntities : []
                const maxLength = Math.max(primaryEntities.length, responseEntities.length)
                const triples = []

                for (let i = 0; i < maxLength; i++) {
                    const primary = primaryEntities[i]
                    const fallback = responseEntities[i]
                    const entity = primary || fallback
                    if (!entity) continue

                    const matchedFallbackEntity = this.findEntityByIdentity(entity, responseEntities)
                    const mention = this.getEntityMentionSnippet(entity) || this.getEntityMentionSnippet(matchedFallbackEntity)

                    triples.push({
                        name: this.entityDisplayName(entity),
                        sentiment: this.getEntitySentimentValue(entity),
                        sentimentLabel: this.sentimentLabelFromValue(this.getEntitySentimentValue(entity)),
                        mention
                    })
                }

                return triples
            },
            getExportHeaders() {
                const uiHeaders = Array.isArray(this.headers) ? [...this.headers] : []
                const ordered = []

                const move = (value) => {
                    const idx = uiHeaders.findIndex((h) => h && h.value === value)
                    if (idx === -1) return
                    ordered.push(uiHeaders[idx])
                    uiHeaders.splice(idx, 1)
                }

                // Export-Reihenfolge (nur CSV): Updated zuerst, Tags direkt nach Prompt
                move("importDate")
                move("platform")
                move("prompt")
                move("tags")
                move("sentiment")
                move("responseText")
                move("mergedEntities")
                move("sources")

                return ordered.concat(uiHeaders)
            },
            buildCsvFromRows(sourceRows = []) {
                const exportHeaders = this.getExportHeaders()
                const baseHeaders = exportHeaders.map((h) => h.text)
                const entityTriplesByRow = sourceRows.map((item) => this.getEntityExportTriples(item))
                const maxEntityRank = entityTriplesByRow.reduce((max, triples) => Math.max(max, triples.length), 0)

                const entityHeaders = []
                for (let i = 1; i <= maxEntityRank; i++) {
                    entityHeaders.push(`Entity Rank ${i}`)
                    entityHeaders.push(`Sentiment Entity Rank ${i}`)
                    entityHeaders.push(`Sentiment Label Entity Rank ${i}`)
                }

                const headers = [...baseHeaders, ...entityHeaders]
                const rows = sourceRows.map((item) =>
                    exportHeaders
                        .map((h) => {
                            if (h.value === "sentiment") {
                                const score = this.getSentimentScore(item)
                                if (score === null) return ""
                                return Number((score * 100).toFixed(0))
                            }

                            let val = item[h.value]

                            // Speziell: nur URLs aus sources exportieren
                            if (h.value === "sources") {
                                if (Array.isArray(val)) {
                                    const urls = val.map((s) => (s && (s.url || s.key || s.domain)) || "").filter(Boolean)
                                    return urls.join(", ")
                                } else if (val && typeof val === "object") {
                                    return val.url || val.key || val.domain || ""
                                }
                                return ""
                            }

                            // Speziell für Entities/MergedEntities: Nur canonicalNames exportieren
                            if (h.value === "mergedEntities" && Array.isArray(val)) {
                                return val.map((e) => e.canonicalName || e.name || "").join(", ")
                            }

                            if (Array.isArray(val)) return val.join(", ")
                            if (typeof val === "object" && val !== null) return JSON.stringify(val)
                            if (typeof val === "string") return val
                            return val !== undefined && val !== null ? val : ""
                        })
                        .concat(
                            (() => {
                                const triples = this.getEntityExportTriples(item)
                                const extraCols = []
                                for (let i = 0; i < maxEntityRank; i++) {
                                    const triple = triples[i] || { name: "", sentiment: "", sentimentLabel: "", mention: "" }
                                    extraCols.push(triple.name)
                                    extraCols.push(triple.sentiment)
                                    extraCols.push(triple.sentimentLabel)
                                }
                                return extraCols
                            })()
                        )
                )
                return [headers, ...rows].map((row) => row.map((cell) => this.csvCell(cell)).join(";")).join("\n")
            },
            buildPromptRequestPayload(skip, limit, sortOverride = null, selectedDaysOverride = null, forceSelectedDatesOnly = false) {
                const sort =
                    sortOverride ||
                    (() => {
                        let s = {}
                        if (this.options.sortBy && this.options.sortBy.length > 0) {
                            s[this.options.sortBy[0]] = this.options.sortDesc && this.options.sortDesc[0] ? -1 : 1
                        } else {
                            s = { importDate: -1 }
                        }
                        return s
                    })()

                const baseBrandName = this.project?.brand?.brand_name || this.brand?.brand_name || null
                const baseBrandMentions = Array.isArray(this.project?.brand?.mentions) ? this.project.brand.mentions : Array.isArray(this.brand?.mentions) ? this.brand.mentions : []

                let post_data = {
                    company_id: localStorage.company_id,
                    project_id: this.project_id || this.$route.params.id,
                    sort,
                    skip,
                    limit,
                    search: this.search
                }

                if (baseBrandName) post_data.brandName = baseBrandName
                if (baseBrandMentions.length) post_data.brandMentions = baseBrandMentions

                const hasDateOverride = Array.isArray(selectedDaysOverride) && selectedDaysOverride.length > 0

                if (this.isAllPlatforms) {
                    const daysForRange = hasDateOverride ? selectedDaysOverride : this.selectedDates
                    if (hasDateOverride && forceSelectedDatesOnly) {
                        post_data.selectedDates = daysForRange
                    } else {
                        const range = this.buildAllPlatformsDateRange(2, daysForRange)
                        if (range) {
                            post_data.selectedDates = [range.from, range.to]
                            post_data.importDateRange = range
                        } else {
                            post_data.selectedDates = Array.isArray(daysForRange) ? daysForRange : []
                        }
                    }
                } else {
                    if (hasDateOverride) {
                        if (forceSelectedDatesOnly) {
                            post_data.selectedDates = selectedDaysOverride
                        } else {
                            const singleRange = this.buildSinglePlatformDateRange(selectedDaysOverride)
                            if (singleRange) {
                                post_data.selectedDates = [singleRange.from, singleRange.to]
                                post_data.importDateRange = singleRange
                            } else {
                                post_data.selectedDates = selectedDaysOverride
                            }
                        }
                    } else {
                        post_data.selectedDates = this.selectedDates
                    }
                    if (this.selectedPlatform && this.selectedPlatform.key) {
                        post_data.platform = this.selectedPlatform.key
                    }
                }

                if (this.tags && this.tags.length) {
                    post_data.tags = this.tags
                    post_data.tagsOperator = this.tagsOperator
                }
                if (Array.isArray(this.filters) && this.filters.length > 0) {
                    post_data.filters = this.filters
                }
                return post_data
            },
            async runWithConcurrency(tasks, limit = 5) {
                const results = new Array(tasks.length)
                let cursor = 0
                const worker = async () => {
                    while (cursor < tasks.length) {
                        const current = cursor++
                        results[current] = await tasks[current]()
                    }
                }
                const workers = []
                for (let i = 0; i < Math.min(limit, tasks.length); i++) {
                    workers.push(worker())
                }
                await Promise.all(workers)
                return results
            },
            async fetchAllRowsForExport(selectedDaysOverride = null, forceSelectedDatesOnly = false) {
                const url =
                    (JSON.parse(process.env.VUE_APP_MONITORING_PROXY_COMPANY_IDS).indexOf(
                        this.CryptoJS.AES.decrypt(localStorage.company_id, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                    ) == -1
                        ? process.env.VUE_APP_MONITORING_API_URL
                        : process.env.VUE_APP_APIURL + "/monitoring-api") + "/ai-suite/promptresponses/get"

                const batchSize = 500

                // Erste Seite laden, um die Gesamtanzahl zu ermitteln
                const firstPayload = this.buildPromptRequestPayload(0, batchSize, null, selectedDaysOverride, forceSelectedDatesOnly)
                const firstResponse = await this.$helpers.axiosPost(url, firstPayload)
                const firstRows = firstResponse?.data?.responses || []
                const total = Number(firstResponse?.data?.total || 0)

                if (!firstRows.length || total <= firstRows.length) {
                    return firstRows
                }

                // Restliche Seiten parallel (mit Concurrency-Limit) laden
                const pageTasks = []
                for (let skip = batchSize; skip < total; skip += batchSize) {
                    const pageSkip = skip
                    pageTasks.push(async () => {
                        const post_data = this.buildPromptRequestPayload(pageSkip, batchSize, null, selectedDaysOverride, forceSelectedDatesOnly)
                        const response_data = await this.$helpers.axiosPost(url, post_data)
                        return response_data?.data?.responses || []
                    })
                }

                const pageResults = await this.runWithConcurrency(pageTasks, 5)
                return firstRows.concat(...pageResults)
            },
            openDownloadDialog() {
                this.downloadScope = "current"
                this.initDownloadDateRange()
                this.downloadDialog = true
                return new Promise((resolve) => {
                    this._downloadResolver = resolve
                })
            },
            cancelDownloadDialog() {
                this.downloadDialog = false
                if (this._downloadResolver) {
                    this._downloadResolver("")
                    this._downloadResolver = null
                }
            },
            async confirmDownloadDialog() {
                if (this.downloadScope === "all" && this.isDownloadDateRangeInvalid) {
                    this.$emit("createNotification", "Please select a valid export date range", "mdi-alert-circle-outline", "warning")
                    return
                }
                if (this.downloadScope === "all") {
                    // Server-seitiger Streaming-Export: ein Request, CSV wird direkt aus der DB gestreamt.
                    const from = this.downloadFromDate <= this.downloadToDate ? this.downloadFromDate : this.downloadToDate
                    const to = this.downloadFromDate <= this.downloadToDate ? this.downloadToDate : this.downloadFromDate
                    const allDays = (this.downloadDateOptions || []).filter((d) => d >= from && d <= to)
                    if (!allDays.length) {
                        this.$emit("createNotification", "No dates in selected range", "mdi-alert-circle-outline", "warning")
                        return
                    }

                    // Parent-Resolver leer beenden, damit er den Download nicht doppelt macht.
                    if (this._downloadResolver) {
                        this._downloadResolver("")
                        this._downloadResolver = null
                    }

                    // Dialog sofort schließen -> Fortschritt läuft im Hintergrund über die Nav-Notifications.
                    this.downloadDialog = false
                    this.streamServerExport(allDays)
                    return
                }

                // Scope "current": kleine, synchrone CSV direkt erzeugen.
                this.downloading = true
                try {
                    const csv = this.buildCsvFromRows(this.prompts)
                    if (this._downloadResolver) {
                        this._downloadResolver(csv)
                        this._downloadResolver = null
                    }
                    this.downloadDialog = false
                } catch (e) {
                    console.error("download export failed", e)
                    this.$emit("createNotification", "Export failed", "mdi-alert-circle-outline", "error")
                    if (this._downloadResolver) {
                        this._downloadResolver("")
                        this._downloadResolver = null
                    }
                    this.downloadDialog = false
                } finally {
                    this.downloading = false
                }
            },
            getPromptApiBaseUrl() {
                return JSON.parse(process.env.VUE_APP_MONITORING_PROXY_COMPANY_IDS).indexOf(
                    this.CryptoJS.AES.decrypt(localStorage.company_id, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                ) == -1
                    ? process.env.VUE_APP_MONITORING_API_URL
                    : process.env.VUE_APP_APIURL + "/monitoring-api"
            },
            getExportColumnsPayload() {
                return this.getExportHeaders().map((h) => ({ value: h.value, text: h.text }))
            },
            async streamServerExport(allDays) {
                const url = this.getPromptApiBaseUrl() + "/ai-suite/promptresponses/export"

                // Payload wie beim normalen Laden aufbauen, aber ohne Paging und mit allen Tagen.
                const payload = this.buildPromptRequestPayload(0, 0, null, allDays, true)
                delete payload.skip
                delete payload.limit
                payload.exportColumns = this.getExportColumnsPayload()

                const authsplits = (process.env.VUE_APP_AUTH || ":").split(":")
                const headers = {
                    "Content-Type": "application/json",
                    Authorization: "Basic " + btoa(`${authsplits[0]}:${authsplits[1]}`)
                }

                const exportId = "prompt_export_" + Date.now()
                this.$root.$emit("localExportStart", { id: exportId, listname: "Prompt Export", message: "Preparing export…" })

                try {
                    const response = await fetch(url, {
                        method: "POST",
                        headers,
                        body: JSON.stringify(payload)
                    })

                    if (!response.ok) {
                        let msg = `Export failed (${response.status})`
                        try {
                            const err = await response.json()
                            if (err && err.error) msg = err.error
                        } catch (_) {}
                        throw new Error(msg)
                    }

                    // Response als Stream lesen, um Fortschritt (Bytes + Zeilen) live anzuzeigen.
                    const filename = `prompts_export_${new Date().toISOString().slice(0, 10)}.csv`
                    let bytes = 0
                    let rows = 0
                    // Quote-bewusstes Zählen: jede Zelle ist in "..." gewrappt, daher ist ein
                    // Zeilenumbruch (0x0A) nur ein echter Datensatz-Trenner, wenn er AUSSERHALB
                    // von Anführungszeichen steht. Eingebettete \n in responseText werden ignoriert.
                    let inQuotes = false
                    let blob
                    if (response.body && response.body.getReader) {
                        const reader = response.body.getReader()
                        const chunks = []
                        let lastUpdate = 0
                        // eslint-disable-next-line no-constant-condition
                        while (true) {
                            const { done, value } = await reader.read()
                            if (done) break
                            chunks.push(value)
                            bytes += value.length
                            for (let i = 0; i < value.length; i++) {
                                const b = value[i]
                                if (b === 34) {
                                    // " -> Quote-Status umschalten ("" hebt sich korrekt wieder auf)
                                    inQuotes = !inQuotes
                                } else if (b === 10 && !inQuotes) {
                                    rows++
                                }
                            }
                            const now = Date.now()
                            if (now - lastUpdate > 200) {
                                lastUpdate = now
                                const mb = (bytes / (1024 * 1024)).toFixed(1)
                                this.$root.$emit("localExportProgress", {
                                    id: exportId,
                                    message: `Downloading ${rows.toLocaleString()} rows · ${mb} MB`
                                })
                            }
                        }
                        blob = new Blob(chunks, { type: "text/csv;charset=utf-8" })
                    } else {
                        // Fallback ohne Streaming-Support.
                        blob = await response.blob()
                        bytes = blob.size
                    }

                    const link = document.createElement("a")
                    link.href = URL.createObjectURL(blob)
                    link.download = filename
                    document.body.appendChild(link)
                    link.click()
                    document.body.removeChild(link)
                    URL.revokeObjectURL(link.href)

                    const mb = (bytes / (1024 * 1024)).toFixed(1)
                    // Der Server schreibt pro Datenzeile einen führenden \n; die Header-Zeile
                    // hat KEINEN führenden \n. Daher = Anzahl \n (außerhalb Quotes) = Datenzeilen.
                    this.$root.$emit("localExportDone", {
                        id: exportId,
                        message: `Export ready · ${rows.toLocaleString()} rows · ${mb} MB`
                    })
                } catch (e) {
                    console.error("stream export failed", e)
                    this.$root.$emit("localExportError", { id: exportId, message: e.message || "Export failed" })
                }
            },
            exportTableData(mode = "current") {
                if (mode === "dialog") return this.openDownloadDialog()
                if (mode === "all") {
                    return this.fetchAllRowsForExport().then((rows) => this.buildCsvFromRows(rows))
                }
                return this.buildCsvFromRows(this.prompts)
            },
            openFilterMenu(fieldKey) {
                this.filterMenuField = null
                this.$nextTick(() => {
                    this.filterMenuField = fieldKey
                    this.filterMenuOpen = true
                })
            },
            filtersUpdated(filters) {
                // Filter-Objekte so anpassen, dass bei Brand Mention die Mentions mitgegeben werden
                filters.forEach((f) => {
                    if (f.field && f.field.key === "mergedEntities") {
                        const brandObj = this.brands.find((b) => b.canonicalName === f.value)
                        if (brandObj) {
                            f.mentions = brandObj.mentions // <--- Mentions ergänzen
                        }
                    }
                    if (f.field && f.field.key === "sentiment" && typeof f.value === "string") {
                        const numeric = parseFloat(f.value)
                        if (!Number.isNaN(numeric)) f.numericValue = numeric
                    }
                })
                this.filters = filters
                this.$emit("filtersUpdated", this.filters)
                this.fetchData()
            },
            buildAllPlatformsDateRange(limitPerPlatform = 2, selectedDays = null) {
                if (!this.project) return null
                const fields = {
                    openai: "processed_dates_ai_tracking_openai",
                    chatgpt: "processed_dates_ai_tracking_chatgpt",
                    google: "processed_dates",
                    google_ai_mode: "processed_dates_ai_tracking_google_ai_mode",
                    gemini: "processed_dates_ai_tracking_gemini",
                    claude: "processed_dates_ai_tracking_claude",
                    perplexity: "processed_dates_ai_tracking_perplexity",
                    grok: "processed_dates_ai_tracking_grok",
                    microsoft_copilot: "processed_dates_ai_tracking_microsoft_copilot",
                    amazon_rufus: "processed_dates_ai_tracking_amazon_rufus"
                }

                const toUtcDay = (d) => {
                    const dt = new Date(d)
                    return isNaN(dt) ? null : dt.toISOString().slice(0, 10)
                }

                // NEU: wenn selectedDays übergeben -> nutze Range vom frühesten TS des ersten Tages
                // bis zum spätesten TS des letzten Tages (plattformübergreifend)
                if (Array.isArray(selectedDays) && selectedDays.length) {
                    const days = selectedDays.map(toUtcDay).filter(Boolean).sort()
                    if (!days.length) return null

                    const firstDay = days[0]
                    const lastDay = days[days.length - 1]

                    let earliest = null
                    let latest = null

                    Object.values(fields).forEach((f) => {
                        const arr = this.project[f]
                        if (!Array.isArray(arr) || !arr.length) return
                        arr.forEach((d) => {
                            const dt = new Date(d)
                            if (isNaN(dt)) return
                            const day = dt.toISOString().slice(0, 10)
                            if (day === firstDay) {
                                if (!earliest || dt < earliest) earliest = dt
                            }
                            if (day === lastDay) {
                                if (!latest || dt > latest) latest = dt
                            }
                        })
                    })

                    const from = earliest ? earliest.toISOString() : `${firstDay}T00:00:00.000Z`
                    const to = latest ? latest.toISOString() : `${lastDay}T23:59:59.999Z`
                    if (new Date(from) > new Date(to)) return null
                    return { from, to }
                }

                // ALT: letzter N-Werte pro Plattform → globale Min/Max
                let all = []
                Object.values(fields).forEach((f) => {
                    const arr = this.project[f]
                    if (Array.isArray(arr) && arr.length) {
                        all = all.concat(arr.slice(-limitPerPlatform))
                    }
                })
                if (!all.length) return null
                const parsed = all
                    .map((d) => {
                        const dt = new Date(d)
                        return isNaN(dt) ? null : dt
                    })
                    .filter(Boolean)
                    .sort((a, b) => a - b)
                if (!parsed.length) return null
                return {
                    from: parsed[0].toISOString(),
                    to: parsed[parsed.length - 1].toISOString()
                }
            },
            async fetchData() {
                console.log(this.initialFiltersReady, "fetchData called with filters:", this.filters, this.initialFiltersReady)
                if (!this.initialFiltersReady) return
                this.loading = true
                const skip = ((this.options.page || 1) - 1) * (this.options.itemsPerPage || 25)
                const limit = this.options.itemsPerPage || 25
                const url =
                    (JSON.parse(process.env.VUE_APP_MONITORING_PROXY_COMPANY_IDS).indexOf(
                        this.CryptoJS.AES.decrypt(localStorage.company_id, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                    ) == -1
                        ? process.env.VUE_APP_MONITORING_API_URL
                        : process.env.VUE_APP_APIURL + "/monitoring-api") + "/ai-suite/promptresponses/get"
                const post_data = this.buildPromptRequestPayload(skip, limit)

                try {
                    const response_data = await this.$helpers.axiosPost(url, post_data)
                    this.prompts = response_data.data.responses || []
                    this.total = response_data.data.total || 0
                } catch (e) {
                    console.error("fetchData error", e)
                } finally {
                    this.loading = false
                }
            },
            normalizeDomainCandidate(value) {
                const trimmed = (value || "").trim()
                if (!trimmed) return null
                const candidate = trimmed
                    .replace(/^https?:\/\//, "")
                    .replace(/^www\./, "")
                    .split("/")[0]
                return /^[a-z0-9.-]+\.[a-z]{2,}$/i.test(candidate) && !candidate.includes(" ") ? candidate : null
            },
            resolveCanonicalBrandLabel(entity) {
                if (!Array.isArray(this.brands) || !this.brands.length || !entity) return null

                const candidateValues = [entity?.canonicalName, entity?.name, entity?.brand_name]
                if (Array.isArray(entity?.mentions)) {
                    entity.mentions.forEach((m) => candidateValues.push(typeof m === "string" ? m : m?.text?.content || m?.content || ""))
                }

                const candidateSet = new Set(candidateValues.map((v) => this.normalizeEntityToken(v)).filter(Boolean))
                if (!candidateSet.size) return null

                for (const b of this.brands) {
                    const canonicalLabel = (b?.canonicalName || b?.brand_name || "").toString().trim()
                    if (!canonicalLabel) continue

                    const brandTokens = new Set()
                    const canonicalToken = this.normalizeEntityToken(canonicalLabel)
                    if (canonicalToken) brandTokens.add(canonicalToken)
                    ;(Array.isArray(b?.mentions) ? b.mentions : []).forEach((m) => {
                        const val = typeof m === "string" ? m : m?.text?.content || m?.content || ""
                        const token = this.normalizeEntityToken(val)
                        if (token) brandTokens.add(token)
                    })

                    for (const token of candidateSet) {
                        if (brandTokens.has(token)) return canonicalLabel
                    }
                }

                return null
            },
            getDisplayEntities(item) {
                const list = Array.isArray(item?.mergedEntities) ? item.mergedEntities : []
                const out = []
                const seen = new Set()

                list.forEach((entity, idx) => {
                    const label = this.entityDisplayName(entity)
                    const labelToken = this.normalizeEntityToken(label)
                    if (!labelToken || seen.has(labelToken)) return
                    seen.add(labelToken)
                    out.push({
                        entity,
                        label,
                        key: entity?._id || `${labelToken}-${idx}`
                    })
                })

                out.sort((a, b) => {
                    const aPrimary = this.isProjectBrandEntity(a) ? 1 : 0
                    const bPrimary = this.isProjectBrandEntity(b) ? 1 : 0
                    if (aPrimary !== bPrimary) return bPrimary - aPrimary
                    return a.label.localeCompare(b.label)
                })

                return out
            },
            getHiddenEntities(item) {
                const all = this.getDisplayEntities(item)
                return all.length > 4 ? all.slice(4) : []
            },
            getProjectBrandEntityTokenSet() {
                const set = new Set()
                const add = (value) => {
                    const token = this.normalizeEntityToken(value)
                    if (token) set.add(token)
                }

                add(this.project?.brand?.brand_name)
                add(this.project?.brand?.canonicalName)
                add(this.project?.brand?.name)
                ;(Array.isArray(this.project?.brand?.mentions) ? this.project.brand.mentions : []).forEach((m) => {
                    if (typeof m === "string") add(m)
                    else add(m?.text?.content || m?.content || m?.name)
                })

                return set
            },
            isProjectBrandEntity(entry) {
                const projectTokens = this.getProjectBrandEntityTokenSet()
                if (!projectTokens.size) return false

                const entity = entry?.entity || entry
                const candidates = [entry?.label, entity?.canonicalName, entity?.name, entity?.brand_name]
                ;(Array.isArray(entity?.mentions) ? entity.mentions : []).forEach((m) => {
                    candidates.push(typeof m === "string" ? m : m?.text?.content || m?.content || m?.name)
                })

                return candidates.some((value) => {
                    const token = this.normalizeEntityToken(value)
                    return token && projectTokens.has(token)
                })
            },
            entityDisplayName(entity) {
                return this.resolveCanonicalBrandLabel(entity) || entity?.canonicalName || entity?.name || entity?.brand_name || "Unknown"
            },
            entityDotColor(entity) {
                const key = this.normalizeEntityToken(this.entityDisplayName(entity))
                return getBrandColorFromName(key)
            },
            entityDotStyle(entity) {
                const color = this.entityDotColor(entity)
                return {
                    backgroundColor: color
                }
            },
            entityChipStyle(entity) {
                const color = this.entityDotColor(entity)
                return {
                    borderColor: `${color}66`,
                    backgroundColor: `${color}10`
                }
            },
            getBrandWebsiteForEntity(item) {
                const official = item?.metadata?.officialWebsite
                const normalizedOfficial = this.normalizeDomainCandidate(official)
                if (normalizedOfficial) return normalizedOfficial

                if (item.mentions && item.mentions.length) {
                    const domains = item.mentions
                        .map((mention) => {
                            const content = typeof mention === "string" ? mention : mention?.text?.content || ""
                            return this.normalizeDomainCandidate(content)
                        })
                        .filter(Boolean)
                    const uniqueDomains = [...new Set(domains)]
                    if (uniqueDomains.length) return uniqueDomains[0]
                    const firstMention = item.mentions[0]
                    const fallbackRaw = typeof firstMention === "string" ? firstMention : firstMention?.text?.content || ""
                    if (fallbackRaw) {
                        const langCode = this.project?.langloc?.country_code.toLowerCase() || ""
                        const fallback = langCode ? `${fallbackRaw}.${langCode}` : fallbackRaw
                        const normalizedFallback = this.normalizeDomainCandidate(fallback)
                        if (normalizedFallback) return normalizedFallback
                    }
                }
                return null
            },
            // getBrandWebsiteForEntity(entity) {
            //     if (!entity) return null
            //     if (!Array.isArray(this.brands)) return null

            //     const toLower = (s) => (typeof s === "string" ? s.toLowerCase() : "")

            //     const extractDomainsFromString = (s) => {
            //         if (!s || typeof s !== "string") return []
            //         const cleaned = s.replace(/^https?:\/\//i, "").replace(/^www\./i, "")
            //         const m = cleaned.match(/[a-z0-9-]+(?:\.[a-z0-9-]+)+/i)
            //         return m ? [m[0].toLowerCase()] : []
            //     }
            //     const extractDomainsFromList = (list) => {
            //         const out = []
            //         ;(list || []).forEach((m) => {
            //             if (typeof m === "string") out.push(...extractDomainsFromString(m))
            //             else if (m) {
            //                 if (m.content) out.push(...extractDomainsFromString(m.content))
            //                 if (m.text?.content) out.push(...extractDomainsFromString(m.text.content))
            //             }
            //         })
            //         return out
            //     }

            //     const entityNames = [entity.canonicalName, entity.name].filter(Boolean).map(toLower)

            //     // Brand-Match robuster
            //     const match = (this.brands || []).find((b) => {
            //         const pool = []
            //         if (b?.canonicalName) pool.push(b.canonicalName)
            //         if (Array.isArray(b?.mentions)) {
            //             b.mentions.forEach((m) => {
            //                 if (typeof m === "string") pool.push(m)
            //                 else if (m) {
            //                     if (m.content) pool.push(m.content)
            //                     if (m.text?.content) pool.push(m.text.content)
            //                 }
            //             })
            //         }
            //         return pool.some((p) => p && entityNames.includes(p.toLowerCase()))
            //     })

            //     // Kandidaten sammeln
            //     const candidates = new Set()

            //     // 0) Domain direkt aus Entity-Name (z. B. "Douglas.de")
            //     entityNames.forEach((n) => extractDomainsFromString(n).forEach((d) => candidates.add(d)))

            //     // 1) Metadaten
            //     if (match?.metadata?.officialWebsite) {
            //         extractDomainsFromString(match.metadata.officialWebsite).forEach((d) => candidates.add(d))
            //     }

            //     // 2) Mentions (Brand + Entity)
            //     extractDomainsFromList(match?.mentions).forEach((d) => candidates.add(d))
            //     extractDomainsFromList(entity.mentions).forEach((d) => candidates.add(d))

            //     const list = Array.from(candidates)
            //     if (!list.length) {
            //         // Konservativer Fallback (Whitelist, niemals Roh-Sprachcode)
            //         const lang = toLower(this.project?.langloc?.language_code || "")
            //         const country = toLower(this.project?.langloc?.country_code || "")
            //         const tldByCountry = {
            //             de: "de",
            //             at: "at",
            //             ch: "ch",
            //             gb: "uk",
            //             uk: "uk",
            //             us: "com",
            //             it: "it",
            //             hu: "hu",
            //             cz: "cz",
            //             hr: "hr",
            //             si: "si",
            //             sk: "sk",
            //             rs: "rs",
            //             ro: "ro",
            //             bg: "bg",
            //             mk: "mk",
            //             es: "es",
            //             fr: "fr",
            //             nl: "nl"
            //         }
            //         const tldByLang = { en: "com", de: "de", it: "it", es: "es", fr: "fr", nl: "nl" }
            //         const tld = tldByCountry[country] || tldByLang[lang] || "com"

            //         const raw = toLower(entity.canonicalName || entity.name || "").replace(/\.[a-z]{2,}$/, "")
            //         const cleaned = raw
            //             .replace(/\b(gmbh|ag|inc|ltd|co|corp|company|sarl|sa|kg|mbh)\b/g, "")
            //             .replace(/&/g, " und ")
            //             .replace(/[^a-z0-9]+/g, "-")
            //             .replace(/-+/g, "-")
            //             .replace(/^-|-$/g, "")
            //         if (cleaned && cleaned.length >= 2) return `${cleaned}.${tld}`
            //         return null
            //     }

            //     // Bevorzugung: erst Country, dann Language, dann gängige TLDs
            //     const prefer = []
            //     const cc = toLower(this.project?.langloc?.country_code || "")
            //     if (cc) prefer.push(cc)
            //     const lc = toLower(this.project?.langloc?.language_code || "")
            //     if (lc && lc !== cc) prefer.push(lc)
            //     prefer.push("com", "de", "net", "org")

            //     for (const t of prefer) {
            //         const hit = list.find((d) => d.endsWith(`.${t}`))
            //         if (hit) return hit
            //     }
            //     return list[0]
            // },

            formatDate(date) {
                if (!date) return ""
                return new Date(date).toLocaleDateString()
            },
            onOptionsUpdate(newOptions) {
                this.options = newOptions
                this.fetchData()
            },
            openDialog(item) {
                this.dialog = false
                this.$nextTick(() => {
                    this.selectedItem = { ...item }
                    this.$nextTick(() => {
                        this.dialog = true
                    })
                })
            },
            renderInlineMarkdown(text) {
                const raw = (text || "").trim()
                if (!raw) return ""

                const paragraphs = raw.split(/\n{2,}/)
                const first = paragraphs[0] || ""
                const second = paragraphs[1] || ""

                const mdImg = /!\[[^\]]*]\([^)]+\)/g
                const htmlImg = /<img\b[^>]*>/gi
                const stripImgs = (s) => (s || "").replace(mdImg, "").replace(htmlImg, "")

                const hasImgMd = /!\[[^\]]*]\([^)]+\)/.test(first)
                const hasImgHtml = /<img\b[^>]*>/i.test(first)
                const firstPlain = stripImgs(first).trim()
                const isTooShort = firstPlain.length > 0 && firstPlain.length < 30

                // Start mit erstem Absatz
                let content = first
                let idx = 1

                // Falls Bild im ersten oder zu kurz -> zweiten anhängen (falls vorhanden)
                if (((hasImgMd || hasImgHtml) && second) || (isTooShort && second)) {
                    content = `${first}\n\n${second}`
                    idx = 2
                }

                // Danach so lange weitere Absätze anhängen, bis >= 30 Zeichen erreicht
                let plain = stripImgs(content).trim()
                while (plain.length < 30 && idx < paragraphs.length) {
                    const next = paragraphs[idx++] || ""
                    if (next.trim()) {
                        content += `\n\n${next}`
                        plain = stripImgs(content).trim()
                    }
                }

                // Für Inline-Vorschau Bilder entfernen
                const withoutImages = stripImgs(content).trim()
                return this.md.renderInline(withoutImages)
            },
            getPlatformInfo(name) {
                // Name normalisieren (z.B. für "openai" → "openai")
                const key = (name || "").toString().trim().toLowerCase()
                const platforms = {
                    openai: {
                        logo: `<svg xmlns="http://www.w3.org/2000/svg" shape-rendering="geometricPrecision" text-rendering="geometricPrecision" image-rendering="optimizeQuality" fill-rule="evenodd" clip-rule="evenodd" viewBox="0 0 512 509.639"><path fill="#fff" d="M115.612 0h280.775C459.974 0 512 52.026 512 115.612v278.415c0 63.587-52.026 115.613-115.613 115.613H115.612C52.026 509.64 0 457.614 0 394.027V115.612C0 52.026 52.026 0 115.612 0z"/><path fill-rule="nonzero" d="M412.037 221.764a90.834 90.834 0 004.648-28.67 90.79 90.79 0 00-12.443-45.87c-16.37-28.496-46.738-46.089-79.605-46.089-6.466 0-12.943.683-19.264 2.04a90.765 90.765 0 00-67.881-30.515h-.576c-.059.002-.149.002-.216.002-39.807 0-75.108 25.686-87.346 63.554-25.626 5.239-47.748 21.31-60.682 44.03a91.873 91.873 0 00-12.407 46.077 91.833 91.833 0 0023.694 61.553 90.802 90.802 0 00-4.649 28.67 90.804 90.804 0 0012.442 45.87c16.369 28.504 46.74 46.087 79.61 46.087a91.81 91.81 0 0019.253-2.04 90.783 90.783 0 0067.887 30.516h.576l.234-.001c39.829 0 75.119-25.686 87.357-63.588 25.626-5.242 47.748-21.312 60.682-44.033a91.718 91.718 0 0012.383-46.035 91.83 91.83 0 00-23.693-61.553l-.004-.005zM275.102 413.161h-.094a68.146 68.146 0 01-43.611-15.8 56.936 56.936 0 002.155-1.221l72.54-41.901a11.799 11.799 0 005.962-10.251V241.651l30.661 17.704c.326.163.55.479.596.84v84.693c-.042 37.653-30.554 68.198-68.21 68.273h.001zm-146.689-62.649a68.128 68.128 0 01-9.152-34.085c0-3.904.341-7.817 1.005-11.663.539.323 1.48.897 2.155 1.285l72.54 41.901a11.832 11.832 0 0011.918-.002l88.563-51.137v35.408a1.1 1.1 0 01-.438.94l-73.33 42.339a68.43 68.43 0 01-34.11 9.12 68.359 68.359 0 01-59.15-34.11l-.001.004zm-19.083-158.36a68.044 68.044 0 0135.538-29.934c0 .625-.036 1.731-.036 2.5v83.801l-.001.07a11.79 11.79 0 005.954 10.242l88.564 51.13-30.661 17.704a1.096 1.096 0 01-1.034.093l-73.337-42.375a68.36 68.36 0 01-34.095-59.143 68.412 68.412 0 019.112-34.085l-.004-.003zm251.907 58.621l-88.563-51.137 30.661-17.697a1.097 1.097 0 011.034-.094l73.337 42.339c21.109 12.195 34.132 34.746 34.132 59.132 0 28.604-17.849 54.199-44.686 64.078v-86.308c.004-.032.004-.065.004-.096 0-4.219-2.261-8.119-5.919-10.217zm30.518-45.93c-.539-.331-1.48-.898-2.155-1.286l-72.54-41.901a11.842 11.842 0 00-5.958-1.611c-2.092 0-4.15.558-5.957 1.611l-88.564 51.137v-35.408l-.001-.061a1.1 1.1 0 01.44-.88l73.33-42.303a68.301 68.301 0 0134.108-9.129c37.704 0 68.281 30.577 68.281 68.281a68.69 68.69 0 01-.984 11.545v.005zm-191.843 63.109l-30.668-17.704a1.09 1.09 0 01-.596-.84v-84.692c.016-37.685 30.593-68.236 68.281-68.236a68.332 68.332 0 0143.689 15.804 63.09 63.09 0 00-2.155 1.222l-72.54 41.9a11.794 11.794 0 00-5.961 10.248v.068l-.05 102.23zm16.655-35.91l39.445-22.782 39.444 22.767v45.55l-39.444 22.767-39.445-22.767v-45.535z"/></svg>`,
                        label: "openai",
                        status: this.platforms.includes("openai") ? "active" : "upgrade"
                    },
                    chatgpt: {
                        logo: `<svg xmlns="http://www.w3.org/2000/svg" shape-rendering="geometricPrecision" text-rendering="geometricPrecision" image-rendering="optimizeQuality" fill-rule="evenodd" clip-rule="evenodd" viewBox="0 0 512 509.639"><path fill="#fff" d="M115.612 0h280.775C459.974 0 512 52.026 512 115.612v278.415c0 63.587-52.026 115.613-115.613 115.613H115.612C52.026 509.64 0 457.614 0 394.027V115.612C0 52.026 52.026 0 115.612 0z"/><path fill-rule="nonzero" d="M412.037 221.764a90.834 90.834 0 004.648-28.67 90.79 90.79 0 00-12.443-45.87c-16.37-28.496-46.738-46.089-79.605-46.089-6.466 0-12.943.683-19.264 2.04a90.765 90.765 0 00-67.881-30.515h-.576c-.059.002-.149.002-.216.002-39.807 0-75.108 25.686-87.346 63.554-25.626 5.239-47.748 21.31-60.682 44.03a91.873 91.873 0 00-12.407 46.077 91.833 91.833 0 0023.694 61.553 90.802 90.802 0 00-4.649 28.67 90.804 90.804 0 0012.442 45.87c16.369 28.504 46.74 46.087 79.61 46.087a91.81 91.81 0 0019.253-2.04 90.783 90.783 0 0067.887 30.516h.576l.234-.001c39.829 0 75.119-25.686 87.357-63.588 25.626-5.242 47.748-21.312 60.682-44.033a91.718 91.718 0 0012.383-46.035 91.83 91.83 0 00-23.693-61.553l-.004-.005zM275.102 413.161h-.094a68.146 68.146 0 01-43.611-15.8 56.936 56.936 0 002.155-1.221l72.54-41.901a11.799 11.799 0 005.962-10.251V241.651l30.661 17.704c.326.163.55.479.596.84v84.693c-.042 37.653-30.554 68.198-68.21 68.273h.001zm-146.689-62.649a68.128 68.128 0 01-9.152-34.085c0-3.904.341-7.817 1.005-11.663.539.323 1.48.897 2.155 1.285l72.54 41.901a11.832 11.832 0 0011.918-.002l88.563-51.137v35.408a1.1 1.1 0 01-.438.94l-73.33 42.339a68.43 68.43 0 01-34.11 9.12 68.359 68.359 0 01-59.15-34.11l-.001.004zm-19.083-158.36a68.044 68.044 0 0135.538-29.934c0 .625-.036 1.731-.036 2.5v83.801l-.001.07a11.79 11.79 0 005.954 10.242l88.564 51.13-30.661 17.704a1.096 1.096 0 01-1.034.093l-73.337-42.375a68.36 68.36 0 01-34.095-59.143 68.412 68.412 0 019.112-34.085l-.004-.003zm251.907 58.621l-88.563-51.137 30.661-17.697a1.097 1.097 0 011.034-.094l73.337 42.339c21.109 12.195 34.132 34.746 34.132 59.132 0 28.604-17.849 54.199-44.686 64.078v-86.308c.004-.032.004-.065.004-.096 0-4.219-2.261-8.119-5.919-10.217zm30.518-45.93c-.539-.331-1.48-.898-2.155-1.286l-72.54-41.901a11.842 11.842 0 00-5.958-1.611c-2.092 0-4.15.558-5.957 1.611l-88.564 51.137v-35.408l-.001-.061a1.1 1.1 0 01.44-.88l73.33-42.303a68.301 68.301 0 0134.108-9.129c37.704 0 68.281 30.577 68.281 68.281a68.69 68.69 0 01-.984 11.545v.005zm-191.843 63.109l-30.668-17.704a1.09 1.09 0 01-.596-.84v-84.692c.016-37.685 30.593-68.236 68.281-68.236a68.332 68.332 0 0143.689 15.804 63.09 63.09 0 00-2.155 1.222l-72.54 41.9a11.794 11.794 0 00-5.961 10.248v.068l-.05 102.23zm16.655-35.91l39.445-22.782 39.444 22.767v45.55l-39.444 22.767-39.445-22.767v-45.535z"/></svg>`,
                        label: "ChatGPT",
                        status: this.platforms.includes("chatgpt") ? "active" : "upgrade"
                    },
                    google: {
                        logo: `<svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"></path><path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"></path><path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"></path><path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"></path></svg>`,
                        label: "Google AIO",
                        status: this.google ? "active" : "upgrade"
                    },
                    gemini: {
                        logo: `<svg fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 65 65"><mask id="maskme" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="65" height="65"><path d="M32.447 0c.68 0 1.273.465 1.439 1.125a38.904 38.904 0 001.999 5.905c2.152 5 5.105 9.376 8.854 13.125 3.751 3.75 8.126 6.703 13.125 8.855a38.98 38.98 0 005.906 1.999c.66.166 1.124.758 1.124 1.438 0 .68-.464 1.273-1.125 1.439a38.902 38.902 0 00-5.905 1.999c-5 2.152-9.375 5.105-13.125 8.854-3.749 3.751-6.702 8.126-8.854 13.125a38.973 38.973 0 00-2 5.906 1.485 1.485 0 01-1.438 1.124c-.68 0-1.272-.464-1.438-1.125a38.913 38.913 0 00-2-5.905c-2.151-5-5.103-9.375-8.854-13.125-3.75-3.749-8.125-6.702-13.125-8.854a38.973 38.973 0 00-5.905-2A1.485 1.485 0 010 32.448c0-.68.465-1.272 1.125-1.438a38.903 38.903 0 005.905-2c5-2.151 9.376-5.104 13.125-8.854 3.75-3.749 6.703-8.125 8.855-13.125a38.972 38.972 0 001.999-5.905A1.485 1.485 0 0132.447 0z" fill="#000"/><path d="M32.447 0c.68 0 1.273.465 1.439 1.125a38.904 38.904 0 001.999 5.905c2.152 5 5.105 9.376 8.854 13.125 3.751 3.75 8.126 6.703 13.125 8.855a38.98 38.98 0 005.906 1.999c.66.166 1.124.758 1.124 1.438 0 .68-.464 1.273-1.125 1.439a38.902 38.902 0 00-5.905 1.999c-5 2.152-9.375 5.105-13.125 8.854-3.749 3.751-6.702 8.126-8.854 13.125a38.973 38.973 0 00-2 5.906 1.485 1.485 0 01-1.438 1.124c-.68 0-1.272-.464-1.438-1.125a38.913 38.913 0 00-2-5.905c-2.151-5-5.103-9.375-8.854-13.125-3.75-3.749-8.125-6.702-13.125-8.854a38.973 38.973 0 00-5.905-2A1.485 1.485 0 010 32.448c0-.68.465-1.272 1.125-1.438a38.903 38.903 0 005.905-2c5-2.151 9.376-5.104 13.125-8.854 3.75-3.749 6.703-8.125 8.855-13.125a38.972 38.972 0 001.999-5.905A1.485 1.485 0 0132.447 0z" fill="url(#prefix__paint0_linear_2001_67)"/></mask><g mask="url(#maskme)"><g filter="url(#prefix__filter0_f_2001_67)"><path d="M-5.859 50.734c7.498 2.663 16.116-2.33 19.249-11.152 3.133-8.821-.406-18.131-7.904-20.794-7.498-2.663-16.116 2.33-19.25 11.151-3.132 8.822.407 18.132 7.905 20.795z" fill="#FFE432"/></g><g filter="url(#prefix__filter1_f_2001_67)"><path d="M27.433 21.649c10.3 0 18.651-8.535 18.651-19.062 0-10.528-8.35-19.062-18.651-19.062S8.78-7.94 8.78 2.587c0 10.527 8.35 19.062 18.652 19.062z" fill="#FC413D"/></g><g filter="url(#prefix__filter2_f_2001_67)"><path d="M20.184 82.608c10.753-.525 18.918-12.244 18.237-26.174-.68-13.93-9.95-24.797-20.703-24.271C6.965 32.689-1.2 44.407-.519 58.337c.681 13.93 9.95 24.797 20.703 24.271z" fill="#00B95C"/></g><g filter="url(#prefix__filter3_f_2001_67)"><path d="M20.184 82.608c10.753-.525 18.918-12.244 18.237-26.174-.68-13.93-9.95-24.797-20.703-24.271C6.965 32.689-1.2 44.407-.519 58.337c.681 13.93 9.95 24.797 20.703 24.271z" fill="#00B95C"/></g><g filter="url(#prefix__filter4_f_2001_67)"><path d="M30.954 74.181c9.014-5.485 11.427-17.976 5.389-27.9-6.038-9.925-18.241-13.524-27.256-8.04-9.015 5.486-11.428 17.977-5.39 27.902 6.04 9.924 18.242 13.523 27.257 8.038z" fill="#00B95C"/></g><g filter="url(#prefix__filter5_f_2001_67)"><path d="M67.391 42.993c10.132 0 18.346-7.91 18.346-17.666 0-9.757-8.214-17.667-18.346-17.667s-18.346 7.91-18.346 17.667c0 9.757 8.214 17.666 18.346 17.666z" fill="#3186FF"/></g><g filter="url(#prefix__filter6_f_2001_67)"><path d="M-13.065 40.944c9.33 7.094 22.959 4.869 30.442-4.972 7.483-9.84 5.987-23.569-3.343-30.663C4.704-1.786-8.924.439-16.408 10.28c-7.483 9.84-5.986 23.57 3.343 30.664z" fill="#FBBC04"/></g><g filter="url(#prefix__filter7_f_2001_67)"><path d="M34.74 51.43c11.135 7.656 25.896 5.524 32.968-4.764 7.073-10.287 3.779-24.832-7.357-32.488C49.215 6.52 34.455 8.654 27.382 18.94c-7.072 10.288-3.779 24.833 7.357 32.49z" fill="#3186FF"/></g><g filter="url(#prefix__filter8_f_2001_67)"><path d="M54.984-2.336c2.833 3.852-.808 11.34-8.131 16.727-7.324 5.387-15.557 6.631-18.39 2.78-2.833-3.853.807-11.342 8.13-16.728 7.324-5.387 15.558-6.631 18.39-2.78z" fill="#749BFF"/></g><g filter="url(#prefix__filter9_f_2001_67)"><path d="M31.727 16.104C43.053 5.598 46.94-8.626 40.41-15.666c-6.53-7.04-21.006-4.232-32.332 6.274s-15.214 24.73-8.683 31.77c6.53 7.04 21.006 4.232 32.332-6.274z" fill="#FC413D"/></g><g filter="url(#prefix__filter10_f_2001_67)"><path d="M8.51 53.838c6.732 4.818 14.46 5.55 17.262 1.636 2.802-3.915-.384-10.994-7.116-15.812-6.731-4.818-14.46-5.55-17.261-1.636-2.802 3.915.383 10.994 7.115 15.812z" fill="#FFEE48"/></g></g><defs><filter id="prefix__filter0_f_2001_67" x="-19.824" y="13.152" width="39.274" height="43.217" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="2.46" result="effect1_foregroundBlur_2001_67"/></filter><filter id="prefix__filter1_f_2001_67" x="-15.001" y="-40.257" width="84.868" height="85.688" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="11.891" result="effect1_foregroundBlur_2001_67"/></filter><filter id="prefix__filter2_f_2001_67" x="-20.776" y="11.927" width="79.454" height="90.916" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="10.109" result="effect1_foregroundBlur_2001_67"/></filter><filter id="prefix__filter3_f_2001_67" x="-20.776" y="11.927" width="79.454" height="90.916" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="10.109" result="effect1_foregroundBlur_2001_67"/></filter><filter id="prefix__filter4_f_2001_67" x="-19.845" y="15.459" width="79.731" height="81.505" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="10.109" result="effect1_foregroundBlur_2001_67"/></filter><filter id="prefix__filter5_f_2001_67" x="29.832" y="-11.552" width="75.117" height="73.758" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="9.606" result="effect1_foregroundBlur_2001_67"/></filter><filter id="prefix__filter6_f_2001_67" x="-38.583" y="-16.253" width="78.135" height="78.758" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="8.706" result="effect1_foregroundBlur_2001_67"/></filter><filter id="prefix__filter7_f_2001_67" x="8.107" y="-5.966" width="78.877" height="77.539" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="7.775" result="effect1_foregroundBlur_2001_67"/></filter><filter id="prefix__filter8_f_2001_67" x="13.587" y="-18.488" width="56.272" height="51.81" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="6.957" result="effect1_foregroundBlur_2001_67"/></filter><filter id="prefix__filter9_f_2001_67" x="-15.526" y="-31.297" width="70.856" height="69.306" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="5.876" result="effect1_foregroundBlur_2001_67"/></filter><filter id="prefix__filter10_f_2001_67" x="-14.168" y="20.964" width="55.501" height="51.571" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="7.273" result="effect1_foregroundBlur_2001_67"/></filter><linearGradient id="prefix__paint0_linear_2001_67" x1="18.447" y1="43.42" x2="52.153" y2="15.004" gradientUnits="userSpaceOnUse"><stop stop-color="#4893FC"/><stop offset=".27" stop-color="#4893FC"/><stop offset=".777" stop-color="#969DFF"/><stop offset="1" stop-color="#BD99FE"/></linearGradient></defs></svg>`,
                        label: "Gemini",
                        status: this.platforms.includes("gemini") ? "active" : "upgrade"
                    },
                    claude: {
                        logo: `<svg xmlns="http://www.w3.org/2000/svg" shape-rendering="geometricPrecision" text-rendering="geometricPrecision" image-rendering="optimizeQuality" fill-rule="evenodd" clip-rule="evenodd" viewBox="0 0 512 509.64"><path fill="#D77655" d="M115.612 0h280.775C459.974 0 512 52.026 512 115.612v278.415c0 63.587-52.026 115.612-115.613 115.612H115.612C52.026 509.639 0 457.614 0 394.027V115.612C0 52.026 52.026 0 115.612 0z"/><path fill="#FCF2EE" fill-rule="nonzero" d="M142.27 316.619l73.655-41.326 1.238-3.589-1.238-1.996-3.589-.001-12.31-.759-42.084-1.138-36.498-1.516-35.361-1.896-8.897-1.895-8.34-10.995.859-5.484 7.482-5.03 10.717.935 23.683 1.617 35.537 2.452 25.782 1.517 38.193 3.968h6.064l.86-2.451-2.073-1.517-1.618-1.517-36.776-24.922-39.81-26.338-20.852-15.166-11.273-7.683-5.687-7.204-2.451-15.721 10.237-11.273 13.75.935 3.513.936 13.928 10.716 29.749 23.027 38.848 28.612 5.687 4.727 2.275-1.617.278-1.138-2.553-4.271-21.13-38.193-22.546-38.848-10.035-16.101-2.654-9.655c-.935-3.968-1.617-7.304-1.617-11.374l11.652-15.823 6.445-2.073 15.545 2.073 6.547 5.687 9.655 22.092 15.646 34.78 24.265 47.291 7.103 14.028 3.791 12.992 1.416 3.968 2.449-.001v-2.275l1.997-26.641 3.69-32.707 3.589-42.084 1.239-11.854 5.863-14.206 11.652-7.683 9.099 4.348 7.482 10.716-1.036 6.926-4.449 28.915-8.72 45.294-5.687 30.331h3.313l3.792-3.791 15.342-20.372 25.782-32.227 11.374-12.789 13.27-14.129 8.517-6.724 16.1-.001 11.854 17.617-5.307 18.199-16.581 21.029-13.75 17.819-19.716 26.54-12.309 21.231 1.138 1.694 2.932-.278 44.536-9.479 24.062-4.347 28.714-4.928 12.992 6.066 1.416 6.167-5.106 12.613-30.71 7.583-36.018 7.204-53.636 12.689-.657.48.758.935 24.164 2.275 10.337.556h25.301l47.114 3.514 12.309 8.139 7.381 9.959-1.238 7.583-18.957 9.655-25.579-6.066-59.702-14.205-20.474-5.106-2.83-.001v1.694l17.061 16.682 31.266 28.233 39.152 36.397 1.997 8.999-5.03 7.102-5.307-.758-34.401-25.883-13.27-11.651-30.053-25.302-1.996-.001v2.654l6.926 10.136 36.574 54.975 1.895 16.859-2.653 5.485-9.479 3.311-10.414-1.895-21.408-30.054-22.092-33.844-17.819-30.331-2.173 1.238-10.515 113.261-4.929 5.788-11.374 4.348-9.478-7.204-5.03-11.652 5.03-23.027 6.066-30.052 4.928-23.886 4.449-29.674 2.654-9.858-.177-.657-2.173.278-22.37 30.71-34.021 45.977-26.919 28.815-6.445 2.553-11.173-5.789 1.037-10.337 6.243-9.2 37.257-47.392 22.47-29.371 14.508-16.961-.101-2.451h-.859l-98.954 64.251-17.618 2.275-7.583-7.103.936-11.652 3.589-3.791 29.749-20.474-.101.102.024.101z"/></svg>`,
                        label: "Claude",
                        status: "coming soon"
                    },
                    perplexity: {
                        logo: `<svg xmlns="http://www.w3.org/2000/svg" shape-rendering="geometricPrecision" text-rendering="geometricPrecision" image-rendering="optimizeQuality" fill-rule="evenodd" clip-rule="evenodd" viewBox="0 0 512 509.64"><path fill="#1F1F1F" d="M115.613 0h280.774C459.974 0 512 52.025 512 115.612v278.415c0 63.587-52.026 115.613-115.613 115.613H115.613C52.026 509.64 0 457.614 0 394.027V115.612C0 52.025 52.026 0 115.613 0z"/><path fill="#fff" fill-rule="nonzero" d="M348.851 128.063l-68.946 58.302h68.946v-58.302zm-83.908 48.709l100.931-85.349v94.942h32.244v143.421h-38.731v90.004l-94.442-86.662v83.946h-17.023v-83.906l-96.596 86.246v-89.628h-37.445V186.365h38.732V90.768l95.309 84.958v-83.16h17.023l-.002 84.206zm-29.209 26.616c-34.955.02-69.893 0-104.83 0v109.375h20.415v-27.121l84.415-82.254zm41.445 0l82.208 82.324v27.051h21.708V203.388c-34.617 0-69.274.02-103.916 0zm-42.874-17.023l-64.669-57.646v57.646h64.669zm13.617 124.076v-95.2l-79.573 77.516v88.731l79.573-71.047zm17.252-95.022v94.863l77.19 70.83c0-29.485-.012-58.943-.012-88.425l-77.178-77.268z"/></svg>`,
                        label: "Perplexity",
                        status: this.project.platforms.includes("perplexity") ? "active" : "upgrade"
                    },
                    grok: {
                        logo: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" ><path d="M18.542 30.532l15.956-11.776c.783-.576 1.902-.354 2.274.545 1.962 4.728 1.084 10.411-2.819 14.315-3.903 3.901-9.333 4.756-14.299 2.808l-5.423 2.511c7.778 5.315 17.224 4 23.125-1.903 4.682-4.679 6.131-11.058 4.775-16.812l.011.011c-1.966-8.452.482-11.829 5.501-18.735C47.759 1.332 47.88 1.166 48 1l-6.602 6.599V7.577l-22.86 22.958M15.248 33.392c-5.582-5.329-4.619-13.579.142-18.339 3.521-3.522 9.294-4.958 14.331-2.847l5.412-2.497c-.974-.704-2.224-1.46-3.659-1.994-6.478-2.666-14.238-1.34-19.505 3.922C6.904 16.701 5.31 24.488 8.045 31.133c2.044 4.965-1.307 8.48-4.682 12.023C2.164 44.411.967 45.67 0 47l15.241-13.608"/></svg>`,
                        label: "Grok",
                        status: this.project.platforms.includes("grok") ? "active" : "upgrade"
                    },
                    google_ai_mode: {
                        logo: `<svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"></path><path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"></path><path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"></path><path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"></path></svg>`,
                        label: "Google AI",
                        status: this.project.platforms.includes("google_ai_mode") ? "active" : "upgrade"
                    },
                    microsoft_copilot: {
                        logo: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 48 48"><path d="M34.142 7.325A4.63 4.63 0 0029.7 4H28.35a4.63 4.63 0 00-4.554 3.794L21.48 20.407l.575-1.965a4.63 4.63 0 014.444-3.33h7.853l3.294 1.282 3.175-1.283h-.926a4.63 4.63 0 01-4.443-3.325l-1.31-4.461z" fill="url(#prefix__paint0_radial_56201_15503)"/><path d="M14.33 40.656A4.63 4.63 0 0018.779 44h2.87a4.63 4.63 0 004.629-4.51l.312-12.163-.654 2.233a4.63 4.63 0 01-4.443 3.329h-7.919l-2.823-1.532-3.057 1.532h.912a4.63 4.63 0 014.447 3.344l1.279 4.423z" fill="url(#prefix__paint1_radial_56201_15503)"/><path d="M29.5 4H13.46c-4.583 0-7.332 6.057-9.165 12.113C2.123 23.29-.72 32.885 7.503 32.885h6.925a4.63 4.63 0 004.456-3.358 2078.617 2078.617 0 014.971-17.156c.843-2.843 1.544-5.284 2.621-6.805C27.08 4.714 28.086 4 29.5 4z" fill="url(#prefix__paint2_linear_56201_15503)"/><path d="M29.5 4H13.46c-4.583 0-7.332 6.057-9.165 12.113C2.123 23.29-.72 32.885 7.503 32.885h6.925a4.63 4.63 0 004.456-3.358 2078.617 2078.617 0 014.971-17.156c.843-2.843 1.544-5.284 2.621-6.805C27.08 4.714 28.086 4 29.5 4z" fill="url(#prefix__paint3_linear_56201_15503)"/><path d="M18.498 44h16.04c4.582 0 7.332-6.058 9.165-12.115 2.171-7.177 5.013-16.775-3.208-16.775h-6.926a4.63 4.63 0 00-4.455 3.358 2084.036 2084.036 0 01-4.972 17.16c-.842 2.843-1.544 5.285-2.62 6.806-.604.852-1.61 1.566-3.024 1.566z" fill="url(#prefix__paint4_radial_56201_15503)"/><path d="M18.498 44h16.04c4.582 0 7.332-6.058 9.165-12.115 2.171-7.177 5.013-16.775-3.208-16.775h-6.926a4.63 4.63 0 00-4.455 3.358 2084.036 2084.036 0 01-4.972 17.16c-.842 2.843-1.544 5.285-2.62 6.806-.604.852-1.61 1.566-3.024 1.566z" fill="url(#prefix__paint5_linear_56201_15503)"/><defs><radialGradient id="prefix__paint0_radial_56201_15503" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="matrix(-10.96051 -13.38922 12.59013 -10.30637 38.005 20.514)"><stop offset=".096" stop-color="#00AEFF"/><stop offset=".773" stop-color="#2253CE"/><stop offset="1" stop-color="#0736C4"/></radialGradient><radialGradient id="prefix__paint1_radial_56201_15503" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="rotate(51.84 -28.201 27.85) scale(15.9912 15.5119)"><stop stop-color="#FFB657"/><stop offset=".634" stop-color="#FF5F3D"/><stop offset=".923" stop-color="#C02B3C"/></radialGradient><radialGradient id="prefix__paint4_radial_56201_15503" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="rotate(109.274 16.301 20.802) scale(38.3873 45.9867)"><stop offset=".066" stop-color="#8C48FF"/><stop offset=".5" stop-color="#F2598A"/><stop offset=".896" stop-color="#FFB152"/></radialGradient><linearGradient id="prefix__paint2_linear_56201_15503" x1="12.5" y1="7.5" x2="14.788" y2="33.975" gradientUnits="userSpaceOnUse"><stop offset=".156" stop-color="#0D91E1"/><stop offset=".487" stop-color="#52B471"/><stop offset=".652" stop-color="#98BD42"/><stop offset=".937" stop-color="#FFC800"/></linearGradient><linearGradient id="prefix__paint3_linear_56201_15503" x1="14.5" y1="4" x2="15.75" y2="32.885" gradientUnits="userSpaceOnUse"><stop stop-color="#3DCBFF"/><stop offset=".247" stop-color="#0588F7" stop-opacity="0"/></linearGradient><linearGradient id="prefix__paint5_linear_56201_15503" x1="42.586" y1="13.346" x2="42.569" y2="21.215" gradientUnits="userSpaceOnUse"><stop offset=".058" stop-color="#F8ADFA"/><stop offset=".708" stop-color="#A86EDD" stop-opacity="0"/></linearGradient></defs></svg>`,
                        label: "Microsoft Copilot",
                        status: this.project.platforms.includes("microsoft_copilot") ? "active" : "upgrade"
                    },
                    amazon_rufus: {
                        logo: AMAZON_RUFUS_LOGO,
                        label: "Amazon Rufus",
                        status: this.project.platforms.includes("amazon_rufus") ? "active" : "upgrade"
                    },
                    gemini_api: {
                        logo: `<svg fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 65 65">
                            <mask id="maskme" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="65" height="65">
                                <path d="M32.447 0c.68 0 1.273.465 1.439 1.125a38.904 38.904 0 001.999 5.905c2.152 5 5.105 9.376 8.854 13.125 3.751 3.75 8.126 6.703 13.125 8.855a38.98 38.98 0 005.906 1.999c.66.166 1.124.758 1.124 1.438 0 .68-.464 1.273-1.125 1.439a38.902 38.902 0 00-5.905 1.999c-5 2.152-9.375 5.105-13.125 8.854-3.749 3.751-6.702 8.126-8.854 13.125a38.973 38.973 0 00-2 5.906 1.485 1.485 0 01-1.438 1.124c-.68 0-1.272-.464-1.438-1.125a38.913 38.913 0 00-2-5.905c-2.151-5-5.103-9.375-8.854-13.125-3.75-3.749-8.125-6.702-13.125-8.854a38.973 38.973 0 00-5.905-2A1.485 1.485 0 010 32.448c0-.68.465-1.272 1.125-1.438a38.903 38.903 0 005.905-2c5-2.151 9.376-5.104 13.125-8.854 3.75-3.749 6.703-8.125 8.855-13.125a38.972 38.972 0 001.999-5.905A1.485 1.485 0 0132.447 0z" fill="#10cfe3"/>
                            </mask>
                            <g mask="url(#maskme)">
                                <rect width="65" height="65" fill="#10cfe3"/>
                            </g>
                            </svg>
                            `,
                        label: "Gemini API",
                        status: this.project.platforms.includes("gemini_api") ? "active" : "upgrade"
                    }
                }
                return platforms[key] || { logo: null, label: name, status: null }
            }
        },

        watch: {
            initialFilters: {
                immediate: true,
                handler(newVal) {
                    if (!Array.isArray(newVal)) return
                    this.filters = newVal
                }
            },
            initialFiltersReady(newVal) {
                if (newVal && this.isActive) this.fetchData()
            },
            brand(newBrand) {
                this.selectedBrand = newBrand
                this.options.page = 1
                if (this.isActive) this.fetchData()
            },
            selectedDates() {
                if (this.isActive) this.fetchData()
            },
            search() {
                if (!this.isActive) return
                clearTimeout(this._searchDebounce)
                this._searchDebounce = setTimeout(() => {
                    this.options.page = 1
                    this.fetchData()
                }, 400)
            },
            selectedPlatform() {
                // Plattformwechsel -> Seite zurücksetzen und neu laden
                this.options.page = 1
                if (this.isActive) this.fetchData()
            },
            tags() {
                this.options.page = 1
                if (this.isActive) this.fetchData()
            },
            isActive(newVal) {
                if (newVal) {
                    this.options.page = 1
                    this.fetchData()
                }
            }
        },
        mounted() {
            console.log("Mounted prompts table with project:", this.project, "isActive:", this.isActive)
            if (this.isActive) this.fetchData()
        }
    }
</script>
<style>
    .senitmentchip {
        border-radius: 15px;
        border: 2px solid rgb(217, 217, 217);
        padding: 4px 8px;
        font-size: 12px;
        font-weight: 500;
        min-width: 50px;
        text-align: center;
        display: inline-block;
        color: #303030;
        border-left: 4px solid #9e9e9e;
        font-weight: 600;
    }

    .senitmentchip--positive {
        border-left-color: #2e7d32;
    }

    .senitmentchip--negative {
        border-left-color: #c62828;
    }

    .senitmentchip--neutral {
        border-left-color: #9e9e9e;
    }
    #monitoringPromptTable tbody tr {
        cursor: pointer;
    }
    .v-card__title.primarycolor,
    .v-card__subtitle.primarycolor {
        /* Fallback auf Standard-Primary, falls CSS-Var nicht verfügbar ist */
        background-color: var(--v-primary-base, #1976d2) !important;
    }
    .tag-color-dot {
        display: inline-block;
        width: 10px;
        height: 10px;
        border: 2px solid rgba(0, 0, 0, 0.08);
        border-radius: 50%;
        margin-right: 6px;
        vertical-align: middle;
    }

    /* Toggle emphasis */
    .tag-op-toggle .v-btn.is-active {
        background-color: rgba(33, 150, 243, 0.12);
        color: #2196f3 !important;
    }
    .theme--dark .tag-op-toggle .v-btn.is-active {
        background-color: rgba(33, 150, 243, 0.24);
    }
    .platform-icon-wrapper {
        display: inline-flex;
        align-items: center;
        justify-content: center;
    }

    .platform-icon {
        width: 30px;
        height: 30px;
        background: #f2f4f6;
        border-radius: 8px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 4px;
        line-height: 0;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
        transition: transform 0.15s, box-shadow 0.15s;
    }

    .theme--dark .platform-icon {
        background: #222;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.6);
    }

    .platform-icon svg {
        width: 20px;
        height: 20px;
        display: block;
    }

    .platform-icon:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 10px -2px rgba(0, 0, 0, 0.22);
    }

    .pt-selection-mode tbody tr {
        cursor: pointer;
    }
    .pt-selection-mode tbody tr:hover {
        background: rgba(33, 150, 243, 0.06);
    }

    /* Checkbox Fade-In beim Aktivieren des Auswahlmodus */
    #monitoringPromptTable .v-data-table__checkbox {
        transition: opacity 0.18s;
    }
    #monitoringPromptTable:not(.pt-selection-mode) .v-data-table__checkbox {
        opacity: 0;
        pointer-events: none;
    }

    /* Actions-Spalte ausblenden im Auswahlmodus (optional) */
    .pt-selection-mode #monitoringPromptTable td:last-child,
    .pt-selection-mode #monitoringPromptTable th:last-child {
        opacity: 0.25;
    }
    .prompt-text-cell {
        font-weight: 600;
        font-size: 13px;
        line-height: 1.3;
    }
    #monitoringPromptTable td:last-child {
        white-space: nowrap;
        width: 54px;
    }
    .prompt-response-text > img {
        width: 32px;
        height: 32px;
        margin-right: 4px;
    }
    #monitoringPromptTable th {
        white-space: nowrap !important;
        text-transform: uppercase !important;
        font-weight: 300 !important;
    }
    #monitoringPromptTable th.col-response,
    #monitoringPromptTable td.col-response {
        min-width: 350px;
        width: auto !important;
    }
    .entity-chip-list {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        max-width: 520px;
        gap: 2px;
    }
    .entity-chip {
        border-radius: 999px !important;
        font-weight: 600;
        letter-spacing: 0.1px;
        transition: all 0.16s ease;
    }
    .entity-chip:hover {
        transform: translateY(-1px);
        box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
    }
    .entity-chip-dot {
        width: 10px;
        height: 10px;
        border-radius: 999px;
        margin-right: 8px;
        flex: 0 0 auto;
        box-shadow: 0 0 0 2px #fff, 0 0 0 3px rgba(0, 0, 0, 0.08);
        position: relative;
        top: 1px;
    }
    .theme--dark .entity-chip-dot {
        box-shadow: 0 0 0 2px #1f1f1f, 0 0 0 3px rgba(255, 255, 255, 0.08);
    }
    .entity-chip-label {
        max-width: 180px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        display: inline-block;
    }
    .entity-chip-more {
        border-radius: 999px !important;
    }
    .entity-more-tooltip {
        min-width: 180px;
        max-width: 320px;
    }
    .entity-more-tooltip-row + .entity-more-tooltip-row {
        margin-top: 4px;
    }
    .entity-more-tooltip-label--primary {
        font-weight: 700;
    }
    .entity-more-tooltip-badge {
        opacity: 0.9;
        margin-left: 4px;
    }
    .v-data-table .v-data-table__wrapper td {
        padding-top: 12px !important;
        padding-bottom: 12px !important;
    }
</style>
